import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  adminAPI,
  contributionsAPI,
  ledgerPlatformAPI,
  membersAPI,
  peoplePlatformAPI,
  positionsAPI,
  settingsAPI,
  usersAPI,
} from '@/services';
import {
  buildUsersPositionIndex,
  mapPeopleListResponse,
  mapPeopleToMemberRow,
  resolveProjectPositionIdForPerson,
} from '../models/memberModel';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import MemberContributionsMobile from '../components/MemberContributionsMobile';
import MemberPaymentFormMobile from '../components/MemberPaymentFormMobile';
import ModalShell from '../components/ModalShell';
import MmSelect from '../components/MmSelect';
import ContributionReceiptModal from '../components/ContributionReceiptModal';
import ContributionInvoiceModal, { isUnpaidContributionStatus } from '../components/ContributionInvoiceModal';
import ContributionPeriodReportModal from '../components/ContributionPeriodReportModal';
import ContributionPaymentsList from '../components/ContributionPaymentsList';
import {
  ContributionActiveFilterBadges,
  ContributionListEmpty,
  ContributionListError,
  ContributionListSkeleton,
  ContributionMemberCell,
  ContributionPaymentsKpiGrid,
  ContributionRowActionsMenu,
  ContributionStatusBadge,
  contributionReference,
  formatContributionAmount,
} from '../components/ContributionPaymentUi';
import TableListCheckbox from '../components/TableListCheckbox';
import TableRowActions from '../components/TableRowActions';
import UiSurface from '../components/UiSurface';
import { parseTabularFile } from '../utils/tabularImport';
import { downloadContributionsImportTemplate, downloadTextFile } from '../utils/importTemplates';
import { useIsMobileSurface } from '../hooks/useUiSurfaceVisible';
import {
  buildExceptionalTypesFromSettings,
  formatExceptionalMonthKey,
  isExceptionalMonthValue,
  parseExceptionalTypeKeyFromMonth,
  typeKeyMatchesMonth,
} from '../utils/exceptionalContributions';
import { harmonizedMemberUnpaidMonthsDisplay } from '../utils/contributionArrearsUi';
import {
  buildArrearsInvoiceContributionRows,
  enrichDuePaymentRows,
  listUnpaidRegularMonthKeys,
  memberIsActiveForArrearsInvoice,
} from '../utils/contributionArrearsInvoice';
import {
  buildMemberDashboardPersonalStats,
  countDistinctPaidRegularMonthsFromRows,
  isAdhesionContributionMonth,
} from '../utils/memberDashboardPersonalStats';
import { fetchLedgerLinkedContributionIds } from '../utils/ledgerEntreaMirror';
import { isMemberLikeEntreaUser, normalizeRoleKeyForPermissions } from '../utils/iamRoles';
import { searchFieldsMatch } from '../utils/searchText';
import { getResolvedPortalTitle } from '../utils/portalBranding';

/** Date du jour (locale) au format YYYY-MM-DD pour les champs `type="date"`. */
function getLocalDateISO() {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function buildContributionsImportLogContent({ created, errors = [], warnings = [] }, t) {
  const ts = new Date();
  const stamp = ts.toISOString();
  const lines = [
    t('contributionsPage.importLog.title', { stamp }),
    t('contributionsPage.importLog.created', { count: Number(created || 0) }),
    t('contributionsPage.importLog.errors', { count: Array.isArray(errors) ? errors.length : 0 }),
    t('contributionsPage.importLog.warnings', { count: Array.isArray(warnings) ? warnings.length : 0 }),
    '',
  ];
  if (Array.isArray(errors) && errors.length > 0) {
    lines.push(t('contributionsPage.importLog.errorsSection'));
    errors.forEach((e, i) => {
      lines.push(
        t('contributionsPage.importLog.errLine', {
          n: i + 1,
          index: e?.index ?? '-',
          msg: e?.error || t('contributionsPage.importLog.unknownError'),
        })
      );
    });
    lines.push('');
  }
  if (Array.isArray(warnings) && warnings.length > 0) {
    lines.push(t('contributionsPage.importLog.warningsSection'));
    warnings.forEach((w, i) => {
      lines.push(
        t('contributionsPage.importLog.warnLine', {
          n: i + 1,
          index: w?.index ?? '-',
          msg: w?.warning || t('contributionsPage.importLog.unknownWarning'),
        })
      );
    });
    lines.push('');
  }
  return lines.join('\n');
}

function csvEscapeCell(value) {
  const s = String(value ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildContributionsImportLogCsv({ created, errors = [], warnings = [] }, t) {
  const ts = new Date().toISOString();
  const rows = [
    ['timestamp', 'created_count', 'errors_count', 'warnings_count', 'entry_type', 'index', 'message'],
    [ts, Number(created || 0), Array.isArray(errors) ? errors.length : 0, Array.isArray(warnings) ? warnings.length : 0, 'summary', '', ''],
  ];
  (Array.isArray(errors) ? errors : []).forEach((e) => {
    rows.push([
      ts,
      Number(created || 0),
      errors.length,
      Array.isArray(warnings) ? warnings.length : 0,
      'error',
      e?.index ?? '',
      e?.error || t('contributionsPage.importLog.unknownError'),
    ]);
  });
  (Array.isArray(warnings) ? warnings : []).forEach((w) => {
    rows.push([
      ts,
      Number(created || 0),
      Array.isArray(errors) ? errors.length : 0,
      warnings.length,
      'warning',
      w?.index ?? '',
      w?.warning || t('contributionsPage.importLog.unknownWarning'),
    ]);
  });
  return rows.map((r) => r.map(csvEscapeCell).join(',')).join('\n');
}

/** Aligne la clé Entrea (settings) sur les `kind` Ledger + résout un `payment_method_id` avec compte lié. */
function resolveLedgerPaymentMethodId(ledgerPaymentMethods, entreaKey) {
  const raw = String(entreaKey || '').trim().toLowerCase();
  if (!raw) return null;
  const kindMap = {
    banque: 'virement',
    bank: 'virement',
    virement: 'virement',
    especes: 'especes',
    espèces: 'especes',
    cheque: 'cheque',
    chèque: 'cheque',
    carte_bancaire: 'carte_bancaire',
    mobile_money: 'mobile_money',
    prelevement: 'prelevement',
    autre: 'autre',
  };
  const kind = kindMap[raw] || raw;
  const withAcc = (ledgerPaymentMethods || []).filter((pm) => pm?.ledger_account_id);
  const byKind = withAcc.find((pm) => String(pm.kind || '').toLowerCase() === kind);
  if (byKind) return byKind.id;
  const byName = withAcc.find((pm) => String(pm.name || '').toLowerCase().includes(raw));
  if (byName) return byName.id;
  const def = withAcc.find((pm) => pm.is_default);
  if (def) return def.id;
  return withAcc[0]?.id || null;
}

function isEntreaContributionLedgerTx(tx, sourceId) {
  return (
    tx?.metadata?.source_app === 'entrea' &&
    tx?.metadata?.source_type === 'contribution' &&
    String(tx?.metadata?.source_id || '') === String(sourceId || '')
  );
}

function monthSortRank(value) {
  const s = String(value || '').trim();
  // Mois normalisés type YYYY-MM : tri calendaire fiable.
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mm = Number(m[2]);
    return Number.isFinite(y) && Number.isFinite(mm) ? y * 100 + mm : Number.MIN_SAFE_INTEGER;
  }
  // Valeurs spéciales (adhesion, exceptional:*) : rang bas, tri alphanumérique ensuite.
  return Number.MIN_SAFE_INTEGER;
}

function isRegularMonthKey(value) {
  return /^\d{4}-\d{2}$/.test(String(value || '').trim());
}

function nextMonthKey(monthKey) {
  const m = String(monthKey || '').trim().match(/^(\d{4})-(\d{2})$/);
  if (!m) return '';
  const y = Number(m[1]);
  const mm = Number(m[2]);
  const dt = new Date(y, mm - 1 + 1, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

/** Montant mensuel strict (tolérance flottante). */
function isMultipleOfMonthlyAmount(amount, monthly) {
  const total = Number(amount || 0);
  const unit = Number(monthly || 0);
  if (!(total > 0) || !(unit > 0)) return false;
  const ratio = total / unit;
  return Math.abs(ratio - Math.round(ratio)) < 1e-6;
}

function buildMonthlySplits(totalAmount, expectedMonthlyAmount, startMonth) {
  const rows = [];
  let remaining = Number(totalAmount || 0);
  const monthly = Number(expectedMonthlyAmount || 0);
  let month = String(startMonth || '').trim();
  if (!(remaining > 0) || !(monthly > 0) || !isRegularMonthKey(month)) return rows;
  // Avec multiples stricts, remaining est toujours un multiple ; garde-fou 120 mois.
  for (let i = 0; i < 120 && remaining > 0; i += 1) {
    const amount = Math.min(monthly, remaining);
    rows.push({ month, amount });
    remaining = Number((remaining - amount).toFixed(2));
    month = nextMonthKey(month);
    if (!month) break;
  }
  return rows;
}

function sortContributions(items, sortBy = 'month', order = 'desc') {
  const mult = order === 'asc' ? 1 : -1;
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    if (sortBy === 'amount') {
      const aa = Number(a?.amount || 0);
      const bb = Number(b?.amount || 0);
      if (aa !== bb) return (aa - bb) * mult;
      const ac = String(a?.created_at || a?.updated_at || '');
      const bc = String(b?.created_at || b?.updated_at || '');
      return bc.localeCompare(ac);
    }
    const am = String(a?.month || '').trim();
    const bm = String(b?.month || '').trim();
    const ar = monthSortRank(am);
    const br = monthSortRank(bm);
    if (ar !== br) return (ar - br) * mult;
    if (am !== bm) return am.localeCompare(bm) * mult;
    const ac = String(a?.created_at || a?.updated_at || '');
    const bc = String(b?.created_at || b?.updated_at || '');
    return bc.localeCompare(ac);
  });
}

const ALL_ITEMS_LIMIT = 10000;

const Contributions = ({ forceStaffView = false }) => {
  const financialSource = (process.env.REACT_APP_FINANCIAL_SOURCE || 'ledger').toLowerCase();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, role } = usePermissions();
  const { user } = useAuth();
  // Certains utilisateurs exposent le rôle sous un alias ("member" vs "membre").
  // On normalise pour que la vue membre s'applique hors `/portal`.
  // Sous `/portal` (forceStaffView), les rôles bureau (executive, etc.) gardent la vue staff
  // même s'ils sont liés à une fiche membre — sinon président = « Mes cotisations » uniquement.
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre =
    !forceStaffView && isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const isMobileSurface = useIsMobileSurface();
  const canMemberCreate =
    isMembre && (hasPermission('contributions.create') || hasPermission('contributions.create_own'));
  /**
   * Droit de payer pour un autre membre.
   * Hors `/portal`, le président/trésorier est remappé en `participant` pour l’UI :
   * on lit donc le rôle / position réels (executive, finance_lead, admin…).
   */
  const canMemberPayForOthers = useMemo(() => {
    if (!isMembre) return false;
    if (hasPermission('contributions.create')) return true;
    const candidates = [
      user?.role,
      role,
      ...(Array.isArray(user?.position_role_keys) ? user.position_role_keys : []),
    ];
    return candidates.some((raw) => {
      const n = normalizeRoleKeyForPermissions(raw);
      return (
        n === 'executive' ||
        n === 'finance_lead' ||
        n === 'admin' ||
        n === 'org_admin' ||
        n === 'organization_admin' ||
        n === 'admin_project'
      );
    });
  }, [isMembre, hasPermission, user?.role, user?.position_role_keys, role]);
  /** Même logique que l’accueil : trésorier (pas les profils avec stats org type président / admin). */
  const useTreasurerDeletionSuccessCopy =
    !isMembre &&
    hasPermission('contributions.delete') &&
    !hasPermission('dashboard.stats');
  const { error, success, warning } = useToast();
  const { confirm } = useConfirmDialog();
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const translateLedgerKind = useCallback((kind) => {
    const raw = String(kind || '').trim().toLowerCase();
    const norm = raw === 'chèque' || raw === 'cheque' ? 'cheque' : raw;
    const key = `contributionsPage.ledgerKind.${norm}`;
    const out = t(key, { defaultValue: '' });
    if (out && out !== key) return out;
    return kind ? String(kind).replace(/_/g, ' ') : '';
  }, [t]);
  const { settings } = useSettings();

  const exceptionalEnabled = Boolean(
    settings?.assistance?.financial_mechanics?.exceptional_contribution_enabled
  );
  const exceptionalTypes = useMemo(
    () => (exceptionalEnabled ? buildExceptionalTypesFromSettings(settings) : []),
    [exceptionalEnabled, settings]
  );

  const [formKind, setFormKind] = useState('monthly');
  
  const [contributions, setContributions] = useState([]);
  /** Dataset complet des cotisations du membre (indépendant pagination/filtres UI). */
  const [memberAllContributions, setMemberAllContributions] = useState([]);
  /** Historique org complet (admin) pour calculer dus / avis — hors pagination/filtres. */
  const [adminAllContributions, setAdminAllContributions] = useState([]);
  /** Stats + historique complets du membre filtré (alignés sur la liste Membres). */
  const [adminSelectedMemberStats, setAdminSelectedMemberStats] = useState(null);
  const [adminSelectedMemberContributions, setAdminSelectedMemberContributions] = useState(null);
  const [members, setMembers] = useState([]); // Toujours un tableau
  const [usersById, setUsersById] = useState({});
  const [filteredContributions, setFilteredContributions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('month');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  /** Choix « pour moi / pour un autre » avant le formulaire (membres avec create). */
  const [showPayForWhomModal, setShowPayForWhomModal] = useState(false);
  /** null | 'self' | 'other' — bénéficiaire du paiement côté membre. */
  const [paymentForWhom, setPaymentForWhom] = useState(null);
  const [showLedgerSetupModal, setShowLedgerSetupModal] = useState(false);
  const [showPeriodReportModal, setShowPeriodReportModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [importingContributions, setImportingContributions] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false); // État de chargement pour la liste uniquement
  const [loadError, setLoadError] = useState('');
  /** Somme des cotisations `pending` (montant encore en attente de validation — vue admin). */
  const [adminPendingAmountSum, setAdminPendingAmountSum] = useState(null);
  /** Montant total toutes cotisations (vue admin, indépendant du filtre membre / pagination). */
  const [adminTotalAmountSum, setAdminTotalAmountSum] = useState(null);
  /** Stats `/members/:id/stats` (mois payés / impayés) — vue membre, KPI harmonisés avec la fiche membre. */
  const [memberKpiStats, setMemberKpiStats] = useState(null);
  const [contributionsPage, setContributionsPage] = useState(1);
  const [contributionsTotal, setContributionsTotal] = useState(0);
  const [itemsPerPageOption, setItemsPerPageOption] = useState('20');
  const isAllItems = itemsPerPageOption === 'all';
  const effectiveItemsPerPage = isAllItems
    ? ALL_ITEMS_LIMIT
    : Math.max(1, Number(itemsPerPageOption) || 20);

  const canBulkSelectContributions =
    !isMembre &&
    !isMobileSurface &&
    selectedStatus !== 'due' &&
    (hasPermission('contributions.approve') || hasPermission('contributions.delete'));
  const canExportContributionReport =
    !isMembre &&
    (hasPermission('contributions.export') ||
      hasPermission('contributions.approve') ||
      hasPermission('contributions.view'));
  const adminPendingApprovalCount = useMemo(() => {
    if (isMembre) return 0;
    const rows = Array.isArray(adminAllContributions) ? adminAllContributions : [];
    return rows.filter((c) => {
      const st = String(c?.status || '').toLowerCase();
      return st === 'pending' || st === 'awaiting';
    }).length;
  }, [isMembre, adminAllContributions]);
  const canShowComptabiliserCotisation = useMemo(
    () =>
      financialSource === 'ledger' &&
      !isMembre &&
      (hasPermission('financial.create') || hasPermission('contributions.approve')),
    [financialSource, isMembre, hasPermission]
  );
  const [selectedContributionIds, setSelectedContributionIds] = useState([]);
  const currentPersonId = String(user?.person_id || user?.member_id || '').trim();
  const [resolvedPersonId, setResolvedPersonId] = useState('');
  const effectivePersonId = currentPersonId || resolvedPersonId;
  const [ledgerPaymentMethods, setLedgerPaymentMethods] = useState([]);
  const [ledgerPmLoading, setLedgerPmLoading] = useState(true);
  /** `null` = non chargé ou non pertinent ; objet id cotisation → true si écriture Ledgera liée. */
  const [ledgerMirrorByContribId, setLedgerMirrorByContribId] = useState(null);
  const [comptabiliserContribId, setComptabiliserContribId] = useState(null);
  /** Positions projet (Président, Trésorier…) pour la cellule membre. */
  const [projectPositions, setProjectPositions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLedgerPmLoading(true);
      try {
        const res = await ledgerPlatformAPI.listPaymentMethods({ include_inactive: false, limit: 200 });
        if (!cancelled) {
          setLedgerPaymentMethods(res?.data?.payment_methods || []);
        }
      } catch (_e) {
        if (!cancelled) setLedgerPaymentMethods([]);
      } finally {
        if (!cancelled) setLedgerPmLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await positionsAPI.getList();
        if (!cancelled) setProjectPositions(res.data?.positions || []);
      } catch {
        if (!cancelled) setProjectPositions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const positionLabelById = useMemo(() => {
    const m = new Map();
    for (const p of projectPositions || []) {
      const id = String(p?.id || '').trim();
      if (!id) continue;
      m.set(id, String(p?.label || p?.key || id));
    }
    return m;
  }, [projectPositions]);

  /**
   * Source des moyens : `financial.payment_methods` s'il est rempli, sinon moyens Ledgera (même logique
   * que Paramètres) avec compte rattaché — noms en valeur pour la résolution côté API.
   */
  const paymentMethodOptions = useMemo(() => {
    const raw = settings?.financial?.payment_methods;
    if (Array.isArray(raw) && raw.length > 0) {
      const seen = new Set();
      return raw
        .map((x) => String(x || '').trim())
        .filter(Boolean)
        .filter((v) => (seen.has(v) ? false : (seen.add(v), true)))
        .map((v) => ({
          value: v,
          label: v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' '),
        }));
    }
    return (ledgerPaymentMethods || [])
      .filter((pm) => pm?.ledger_account_id)
      .map((pm) => {
        const name = String(pm.name || '').trim();
        const kind = String(pm.kind || '').trim().toLowerCase();
        const value = name ? name.toLowerCase() : kind;
        const kindLabel = translateLedgerKind(kind) || (kind ? kind.replace(/_/g, ' ') : '');
        const label = name || kindLabel || t('contributionsPage.defaultPmLabel');
        return { value, label };
      })
      .filter((row) => row.value);
  }, [settings?.financial?.payment_methods, ledgerPaymentMethods, t, translateLedgerKind]);

  const paymentMethods = useMemo(
    () => paymentMethodOptions.map((o) => o.value),
    [paymentMethodOptions]
  );

  const defaultPaymentMethod = useMemo(() => {
    const def = String(settings?.financial?.default_payment_method || '').trim();
    if (def && paymentMethods.includes(def)) return def;
    const withAcc = (ledgerPaymentMethods || []).filter((pm) => pm?.ledger_account_id);
    const d = withAcc.find((pm) => pm.is_default);
    if (d) {
      const name = String(d.name || '').trim();
      const v = name ? name.toLowerCase() : String(d.kind || '').trim().toLowerCase();
      if (v && paymentMethods.includes(v)) return v;
    }
    return paymentMethods[0] || '';
  }, [settings?.financial?.default_payment_method, paymentMethods, ledgerPaymentMethods]);

  const [formData, setFormData] = useState({
    member_id: '',
    person_id: '',
    amount: 5000,
    month: '',
    status: 'pending',
    payment_method: '',
    notes: '',
    proof_documents: []
  });
  const [uploadedFiles, setUploadedFiles] = useState([]); // Liste d'objets {file_url, description}
  const [uploading, setUploading] = useState(false);
  const [selectedMemberLastPaidMonth, setSelectedMemberLastPaidMonth] = useState('');
  const [selectedMemberLastPaidLoading, setSelectedMemberLastPaidLoading] = useState(false);
  const contributionsImportInputRef = useRef(null);
  const [bulkFormKind, setBulkFormKind] = useState('monthly');
  const [bulkData, setBulkData] = useState({
    member_ids: [],
    month: '',
    amount: 5000,
    payment_method: '',
    payment_date: ''
  });
  const bulkSelectAllRef = useRef(null);
  const allBulkMemberIds = useMemo(
    () => (Array.isArray(members) ? members.map((m) => m.id).filter(Boolean) : []),
    [members]
  );

  /** Options select : mêmes moyens + valeur d’une cotisation héritée absente de la liste courante. */
  const formPaymentOptions = useMemo(() => {
    const fm = String(formData.payment_method || '').trim();
    if (fm && !paymentMethodOptions.some((o) => o.value === fm)) {
      return [
        ...paymentMethodOptions,
        { value: fm, label: fm.charAt(0).toUpperCase() + fm.slice(1).replace(/_/g, ' ') },
      ];
    }
    return paymentMethodOptions;
  }, [paymentMethodOptions, formData.payment_method]);

  const bulkPaymentOptions = useMemo(() => {
    const bm = String(bulkData.payment_method || '').trim();
    if (bm && !paymentMethodOptions.some((o) => o.value === bm)) {
      return [
        ...paymentMethodOptions,
        { value: bm, label: bm.charAt(0).toUpperCase() + bm.slice(1).replace(/_/g, ' ') },
      ];
    }
    return paymentMethodOptions;
  }, [paymentMethodOptions, bulkData.payment_method]);

  useEffect(() => {
    // Réinitialiser à la page 1 quand les filtres changent
    setContributionsPage(1);
  }, [selectedMember, selectedMonth, selectedStatus, searchQuery]);

  useEffect(() => {
    setContributionsPage(1);
  }, [itemsPerPageOption]);

  useEffect(() => {
    setSelectedContributionIds([]);
  }, [contributionsPage, selectedMember, selectedMonth, selectedStatus, searchQuery, itemsPerPageOption]);

  useEffect(() => {
    let rows = Array.isArray(contributions) ? contributions : [];
    if (isMembre) {
      const myId = String(effectivePersonId || '').trim();
      if (!myId) {
        rows = [];
      } else {
        rows = rows.filter((c) => String(c?.person_id || c?.member_id || '').trim() === myId);
      }
    } else if (searchQuery.trim()) {
      rows = rows.filter((c) => {
        const mid = String(c?.person_id || c?.member_id || '').trim();
        const member = Array.isArray(members)
          ? members.find((m) => String(m?.id || '').trim() === mid)
          : null;
        return searchFieldsMatch(searchQuery, [
          member?.name,
          member?.email,
          member?.phone,
          member?.member_number,
          c?.member_name,
          c?.person_name,
          c?.month,
          c?.notes,
          c?.status,
          c?.amount,
          c?.id,
        ]);
      });
    }
    setFilteredContributions(sortContributions(rows, sortBy, sortOrder));
  }, [contributions, sortBy, sortOrder, isMembre, effectivePersonId, searchQuery, members]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortOrder('desc');
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) return <span className="text-gray-300">↕</span>;
    return <span className="text-entrea-royal">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const loadAdminContributionStats = async () => {
    if (isMembre) return;
    // Les KPI montant (total / pending) sont dérivés de `adminAllContributions` (loadData).
    // L’endpoint summary reste un filet si l’historique n’est pas encore disponible.
    try {
      const res = await contributionsAPI.getStatsSummary();
      const data = res?.data && typeof res.data === 'object' ? res.data : {};
      setAdminTotalAmountSum((prev) => (prev == null ? Number(data.total_amount) || 0 : prev));
      setAdminPendingAmountSum((prev) => (prev == null ? Number(data.pending_amount) || 0 : prev));
    } catch {
      // ignore — loadData recalcule depuis l’historique
    }
  };

  useEffect(() => {
    // Chargement initial (avec le spinner principal)
    loadData(true);
    loadAdminContributionStats();
  }, [role, user?.member_id]);

  useEffect(() => {
    // Rechargement de la liste uniquement (sans spinner principal) quand les filtres ou la page changent
    if (!loading) { // Éviter de recharger si on est déjà en train de charger initialement
      loadData(false);
    }
  }, [
    contributionsPage,
    selectedMember,
    selectedMonth,
    selectedStatus,
    searchQuery,
    effectiveItemsPerPage,
    isAllItems,
    effectivePersonId,
    canMemberPayForOthers,
  ]);

  useEffect(() => {
    const resolvePersonIdFromIamUser = async () => {
      if (!isMembre || currentPersonId) {
        setResolvedPersonId('');
        return;
      }
      const userIdCandidates = [
        user?.id,
        user?.sub,
        user?.user_id,
      ].map((v) => String(v || '').trim()).filter(Boolean);
      for (const uid of userIdCandidates) {
        try {
          const res = await usersAPI.getById(uid);
          const personId = String(res?.data?.person_id || res?.data?.member_id || '').trim();
          if (personId) {
            setResolvedPersonId(personId);
            return;
          }
        } catch (_err) {
          // Ignore and continue with next candidate.
        }
      }
      setResolvedPersonId('');
    };
    resolvePersonIdFromIamUser();
  }, [isMembre, currentPersonId, user?.id, user?.sub, user?.user_id]);

  // Quand les méthodes paramétrées changent : aligner les brouillons (hors édition d’une cotisation existante)
  useEffect(() => {
    if (selectedContribution) return;
    if (paymentMethods.length === 0) {
      setFormData((prev) => ({ ...prev, payment_method: '' }));
      setBulkData((prev) => ({ ...prev, payment_method: '' }));
      return;
    }
    setFormData((prev) => {
      const p = prev.payment_method;
      if (p && paymentMethods.includes(p)) return prev;
      return { ...prev, payment_method: defaultPaymentMethod };
    });
    setBulkData((prev) => {
      const p = prev.payment_method;
      if (p && paymentMethods.includes(p)) return prev;
      return { ...prev, payment_method: defaultPaymentMethod };
    });
  }, [paymentMethods, defaultPaymentMethod, selectedContribution]);

  useEffect(() => {
    const el = bulkSelectAllRef.current;
    if (!el) return;
    const n = allBulkMemberIds.length;
    const c = bulkData.member_ids.length;
    el.indeterminate = n > 0 && c > 0 && c < n;
  }, [allBulkMemberIds, bulkData.member_ids]);

  const loadData = async (isInitialLoad = false) => {
    try {
      setLoadError('');
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setLoadingList(true);
      }
      const params = {
        limit: effectiveItemsPerPage,
        skip: isAllItems ? 0 : (contributionsPage - 1) * effectiveItemsPerPage
      };
      // En mode membre, forcer le périmètre sur la personne connectée.
      if (isMembre && effectivePersonId) {
        params.person_id = effectivePersonId;
        params.member_id = effectivePersonId;
      }
      
      // Ajouter les filtres aux paramètres
      if (selectedMember !== 'all') {
        params.person_id = selectedMember;
        params.member_id = selectedMember;
      }
      if (selectedMonth !== 'all') {
        params.month = selectedMonth;
      }
      if (selectedStatus !== 'all' && selectedStatus !== 'due') {
        params.status = selectedStatus;
      }
      if (isMembre && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const promises = [contributionsAPI.getAll(params)];
      if (isMembre && effectivePersonId) {
        promises.push(
          contributionsAPI.getAll({
            limit: ALL_ITEMS_LIMIT,
            skip: 0,
            person_id: effectivePersonId,
            member_id: effectivePersonId,
          })
        );
      } else {
        promises.push(Promise.resolve({ data: { contributions: [] } }));
      }
      
      // Charger les membres : vue membre restreinte, sauf s’il peut payer pour d’autres.
      if (isMembre && effectivePersonId && !canMemberPayForOthers) {
        promises.push(peoplePlatformAPI.getById(effectivePersonId));
      } else if (!isMembre || (isMembre && canMemberPayForOthers)) {
        promises.push(peoplePlatformAPI.list({ limit: 1000, skip: 0 }));
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }
      if (!isMembre || canMemberPayForOthers) {
        promises.push(usersAPI.getAll({ limit: 2000, skip: 0 }));
      } else {
        promises.push(Promise.resolve({ data: { users: [] } }));
      }
      
      const [contributionsRes, memberAllRes, membersRes, usersRes] = await Promise.all(promises);
      
      // Gérer les deux formats de réponse pour contributions
      if (Array.isArray(contributionsRes.data)) {
        setContributions(contributionsRes.data);
        setContributionsTotal(contributionsRes.data.length);
      } else {
        const contributionsList = contributionsRes.data.contributions || [];
        setContributions(contributionsList);
        setContributionsTotal(contributionsRes.data.total || 0);
      }
      if (isMembre) {
        const fullRows = Array.isArray(memberAllRes?.data)
          ? memberAllRes.data
          : (memberAllRes?.data?.contributions || []);
        setMemberAllContributions(Array.isArray(fullRows) ? fullRows : []);
      } else {
        setMemberAllContributions([]);
      }
      
      let membersList = [];
      if (isMembre && !canMemberPayForOthers && membersRes.data) {
        membersList = [mapPeopleToMemberRow(membersRes.data)];
      } else if (membersRes.data) {
        membersList = mapPeopleListResponse(membersRes.data);
      }
      const usersList = Array.isArray(usersRes?.data)
        ? usersRes.data
        : (usersRes?.data?.users || []);
      const safeUsers = Array.isArray(usersList) ? usersList : [];
      const projectId = localStorage.getItem('project_id') || null;
      const positionIndex = buildUsersPositionIndex(safeUsers, projectId);
      membersList = (Array.isArray(membersList) ? membersList : []).map((m) => ({
        ...m,
        position_id: resolveProjectPositionIdForPerson(m, positionIndex) || '',
      }));
      setMembers(membersList);
      const index = safeUsers.reduce((acc, item) => {
        const key = String(item?.id || '').trim();
        if (key) acc[key] = item;
        return acc;
      }, {});
      setUsersById(index);

      if (isMembre && effectivePersonId) {
        try {
          const stRes = await membersAPI.getStats(effectivePersonId);
          setMemberKpiStats(stRes?.data && typeof stRes.data === 'object' ? stRes.data : null);
        } catch (_e) {
          setMemberKpiStats(null);
        }
      } else {
        setMemberKpiStats(null);
      }

      if (!isMembre) {
        try {
          const allRes = await contributionsAPI.getAll({ limit: ALL_ITEMS_LIMIT, skip: 0 });
          const rows = Array.isArray(allRes.data) ? allRes.data : (allRes.data?.contributions || []);
          const allRows = Array.isArray(rows) ? rows : [];
          setAdminAllContributions(allRows);
          setAdminTotalAmountSum(allRows.reduce((s, c) => s + Number(c.amount || 0), 0));
          setAdminPendingAmountSum(
            allRows
              .filter((c) => {
                const st = String(c?.status || '').toLowerCase();
                return st === 'pending' || st === 'awaiting';
              })
              .reduce((s, c) => s + Number(c.amount || 0), 0)
          );
        } catch {
          setAdminAllContributions([]);
          setAdminTotalAmountSum(null);
          setAdminPendingAmountSum(null);
        }
      } else {
        setAdminAllContributions([]);
        setAdminTotalAmountSum(null);
        setAdminPendingAmountSum(null);
      }

      if (canShowComptabiliserCotisation) {
        try {
          const map = await fetchLedgerLinkedContributionIds(ledgerPlatformAPI);
          setLedgerMirrorByContribId(map);
        } catch (_e) {
          setLedgerMirrorByContribId({});
        }
      } else {
        setLedgerMirrorByContribId(null);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      const msg = t('contributionsPage.errors.loadData');
      setLoadError(msg);
      error(msg);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setLoadingList(false);
      }
    }
  };

  // Alignement liste Membres : stats unpaid_months + historique complet du membre filtré
  useEffect(() => {
    if (isMembre || selectedMember === 'all') {
      setAdminSelectedMemberStats(null);
      setAdminSelectedMemberContributions(null);
      return;
    }
    const pid = String(selectedMember || '').trim();
    if (!pid) {
      setAdminSelectedMemberStats(null);
      setAdminSelectedMemberContributions(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [stRes, coRes] = await Promise.all([
          membersAPI.getStats(pid).catch(() => null),
          contributionsAPI.getAll({
            limit: ALL_ITEMS_LIMIT,
            skip: 0,
            person_id: pid,
            member_id: pid,
          }),
        ]);
        if (cancelled) return;
        const st = stRes?.data && typeof stRes.data === 'object' ? stRes.data : null;
        setAdminSelectedMemberStats(st);
        const rows = Array.isArray(coRes?.data) ? coRes.data : coRes?.data?.contributions || [];
        setAdminSelectedMemberContributions(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) {
          setAdminSelectedMemberStats(null);
          setAdminSelectedMemberContributions(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isMembre, selectedMember]);

  const findLedgerPendingContributionTx = async (sourceId) => {
    const res = await ledgerPlatformAPI.listTransactions({ status: 'pending_validation', limit: 500 });
    const txs = res?.data?.transactions || [];
    return txs.find((tx) => isEntreaContributionLedgerTx(tx, sourceId));
  };

  /** Transaction Ledger liée à la cotisation (en attente ou déjà postée). */
  const findLedgerContributionTx = async (sourceId) => {
    const [pendingRes, postedRes] = await Promise.all([
      ledgerPlatformAPI.listTransactions({
        status: 'pending_validation',
        category: 'cotisation',
        limit: 500,
      }),
      ledgerPlatformAPI.listTransactions({ status: 'posted', category: 'cotisation', limit: 500 }),
    ]);
    const txs = [
      ...(pendingRes?.data?.transactions || []),
      ...(postedRes?.data?.transactions || []),
    ];
    return txs.find((tx) => isEntreaContributionLedgerTx(tx, sourceId));
  };

  /**
   * Après suppression Entrea : rejette l’écriture en attente dans Ledgera, ou avertit si déjà postée.
   */
  const syncLedgerAfterContributionDeleted = async (sourceId) => {
    if (financialSource !== 'ledger') return;
    try {
      const tx = await findLedgerContributionTx(sourceId);
      if (!tx?.id) return;
      const st = String(tx.status || '');
      if (st === 'pending_validation') {
        await ledgerPlatformAPI.rejectTransaction(tx.id, t('contributionsPage.ledger.rejectReason'));
        return;
      }
      if (st === 'posted') {
        warning(t('contributionsPage.warnings.ledgerPosted'));
      }
    } catch (e) {
      console.warn('Ledger: synchronisation après suppression cotisation', e);
      warning(t('contributionsPage.warnings.ledgerSyncFail'));
    }
  };

  const mirrorContributionToLedger = async (contrib, forcePosted = false) => {
    if (financialSource !== 'ledger') return;
    const amount = Number(contrib?.amount || 0);
    if (!(amount > 0)) return;
    const isPending = !forcePosted && contrib?.status !== 'completed';

    let payment_method_id;
    let account_id;
    try {
      const [pmRes, accRes] = await Promise.all([
        ledgerPlatformAPI.listPaymentMethods({ include_inactive: false, limit: 200 }),
        ledgerPlatformAPI.listAccounts({ include_inactive: false }),
      ]);
      const pmList = pmRes?.data?.payment_methods || [];
      payment_method_id = resolveLedgerPaymentMethodId(pmList, contrib?.payment_method);
      if (!payment_method_id) {
        const accounts = accRes?.data?.accounts || [];
        account_id = accounts[0]?.id;
      }
    } catch (e) {
      console.warn('Ledger: résolution compte / moyen de paiement', e);
    }

    if (!payment_method_id && !account_id) {
      throw new Error(t('contributionsPage.warnings.ledgerPm'));
    }

    await ledgerPlatformAPI.createTransaction({
      tx_type: 'credit',
      category: 'cotisation',
      amount,
      description: t('contributionsPage.ledger.descTemplate', {
        month: contrib?.month || '',
        id: contrib?.person_id || contrib?.member_id || '',
      }),
      occurred_at: contrib?.month ? `${String(contrib.month)}-01` : new Date().toISOString().slice(0, 10),
      pending_validation: isPending,
      person_id: contrib?.person_id || contrib?.member_id || undefined,
      ...(payment_method_id ? { payment_method_id } : {}),
      ...(account_id ? { account_id } : {}),
      metadata: {
        source_app: 'entrea',
        source_type: 'contribution',
        source_id: String(contrib?.id || ''),
        person_id: contrib?.person_id || contrib?.member_id || undefined,
        entrea_payment_method: contrib?.payment_method || undefined,
      },
    });
  };

  const contributionNeedsComptabilisation = (contrib) => {
    if (!canShowComptabiliserCotisation || ledgerMirrorByContribId === null) return false;
    const st = String(contrib?.status || '').toLowerCase();
    if (st === 'rejected') return false;
    if (!['pending', 'completed'].includes(st)) return false;
    if (!(Number(contrib?.amount || 0) > 0)) return false;
    const cid = String(contrib?.id || '').trim();
    if (!cid) return false;
    return !ledgerMirrorByContribId[cid];
  };

  const handleComptabiliserContribution = async (contrib) => {
    if (!contributionNeedsComptabilisation(contrib)) return;
    const id = String(contrib?.id || '').trim();
    setComptabiliserContribId(id);
    try {
      const forcePosted = String(contrib?.status || '').toLowerCase() === 'completed';
      await mirrorContributionToLedger(contrib, forcePosted);
      success(t('contributionsPage.success.ledgerWrite'));
      const map = await fetchLedgerLinkedContributionIds(ledgerPlatformAPI);
      setLedgerMirrorByContribId(map);
    } catch (err) {
      console.error(err);
      error(
        err?.message ||
          err?.response?.data?.detail ||
          t('contributionsPage.ledger.ledgerWriteFail')
      );
    } finally {
      setComptabiliserContribId(null);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const personId = getFormPersonId();
    if (!selectedContribution && !personId) {
      error(t('contributionsPage.errors.personIdRequired'));
      return;
    }
    
    if (!ledgerPmLoading && paymentMethods.length === 0) {
      setShowLedgerSetupModal(true);
      return;
    }
    if (ledgerPmLoading && paymentMethods.length === 0) {
      error(t('contributionsPage.errors.pmLoading'));
      return;
    }
    if (!formData.payment_method || formData.payment_method.trim() === '') {
      error(t('contributionsPage.errors.paymentMethodRequired'));
      return;
    }

    const isExceptionalSubmit = selectedContribution
      ? isExceptionalMonthValue(selectedContribution.month)
      : formKind === 'exceptional';
    const selectedMonthKey = String(formData.month || '').trim().slice(0, 7);
    const hasSelectedMonth = isRegularMonthKey(selectedMonthKey);
    // Sans mois choisi (création mensuelle) → répartition auto ; avec mois → une seule ligne.
    const shouldAutoAllocate = !selectedContribution && !isExceptionalSubmit && !hasSelectedMonth;
    const expectedMonthlyAmount = Number(settings?.financial?.contribution_amount || 0);
    if (!isExceptionalSubmit && expectedMonthlyAmount > 0) {
      if (!isMultipleOfMonthlyAmount(formData.amount, expectedMonthlyAmount)) {
        error(
          t('contributionsPage.errors.amountNotMultiple', {
            monthly: expectedMonthlyAmount.toLocaleString(localeFmt),
          })
        );
        return;
      }
    }
    if (!isExceptionalSubmit && !shouldAutoAllocate && !hasSelectedMonth) {
      error(t('contributionsPage.errors.monthRequired'));
      return;
    }
    if (isExceptionalSubmit && !parseExceptionalTypeKeyFromMonth(formData.month)) {
      error(t('contributionsPage.errors.exceptionalTypeRequired'));
      return;
    }
    const monthBucket =
      !isExceptionalSubmit && !shouldAutoAllocate ? String(formData.month || '').trim().slice(0, 7) : '';
    const targetMemberId = selectedContribution
      ? String(getContributionMemberId(selectedContribution) || '').trim()
      : String(personId || '').trim();
    if (monthBucket && targetMemberId) {
      const duplicateLocal = (contributions || []).some((c) => {
        const sameMember = String(getContributionMemberId(c) || '').trim() === targetMemberId;
        const sameMonth = String(c?.month || '').trim().slice(0, 7) === monthBucket;
        const notSelf = !selectedContribution || String(c?.id || '') !== String(selectedContribution?.id || '');
        return sameMember && sameMonth && notSelf;
      });
      if (duplicateLocal) {
        error(t('contributionsPage.errors.duplicateMonth'));
        return;
      }
    }
    
    // Utiliser uploadedFiles comme source de vérité (reflète l'état actuel après ajouts/suppressions)
    const proofDocs = uploadedFiles;
    
    // Si on a des pièces justificatives, vérifier que chaque pièce a une description
    if (proofDocs.length > 0) {
      const invalidProofDocs = proofDocs.filter(doc => {
        const docObj = typeof doc === 'string' ? { file_url: doc, description: '' } : doc;
        return !docObj.description || docObj.description.trim() === '' || !docObj.file_url;
      });
      
      if (invalidProofDocs.length > 0) {
        error(t('contributionsPage.errors.proofDescription', { count: invalidProofDocs.length }));
        return;
      }
    }
    
    try {
      // Côté membre : à la création, forcer soi-même sauf paiement « pour un autre ».
      // Président / bureau qui saisit pour un autre → statut Payée (completed) directement.
      const payForOther =
        isMembre &&
        canMemberCreate &&
        !selectedContribution &&
        canMemberPayForOthers &&
        paymentForWhom === 'other' &&
        String(formData.person_id || '').trim();
      if (isMembre && canMemberCreate && !selectedContribution) {
        if (!payForOther) {
          formData.member_id = effectivePersonId;
          formData.person_id = effectivePersonId;
          formData.status = 'pending';
        } else {
          formData.status = 'completed';
        }
      }
      
      // Convertir les proof_documents au bon format (objets avec file_url et description)
      const formattedProofDocs = proofDocs.map(doc => {
        if (typeof doc === 'string') {
          // Ancien format (rétrocompatibilité) - utiliser description vide
          return { file_url: doc, description: '' };
        }
        // Nouveau format - s'assurer qu'on a file_url et description
        return {
          file_url: doc.file_url || doc.url || '',
          description: doc.description || ''
        };
      }).filter(doc => doc.file_url && doc.file_url.trim() !== ''); // Filtrer les entrées invalides
      
      const exceptionalMonthNorm = isExceptionalSubmit
        ? formatExceptionalMonthKey(
            parseExceptionalTypeKeyFromMonth(formData.month) ||
              (selectedContribution && parseExceptionalTypeKeyFromMonth(selectedContribution.month)) ||
              exceptionalTypes[0]?.key ||
              'adhesion'
          )
        : formData.month;
      const submitData = {
        ...formData,
        person_id: personId,
        month: exceptionalMonthNorm,
        proof_documents: formattedProofDocs.length > 0 ? formattedProofDocs : null
      };
      delete submitData.member_id;
      if (selectedContribution) {
        delete submitData.person_id;
        // Si un membre corrige une cotisation rejetée, elle repasse en attente de validation.
        if (
          isMembre &&
          canMemberCreate &&
          String(selectedContribution?.status || '').toLowerCase() === 'rejected'
        ) {
          submitData.status = 'pending';
        }
      }
      
      if (shouldAutoAllocate) {
        if (!(expectedMonthlyAmount > 0)) {
          error(t('contributionsPage.form.autoAllocateNoMonthlyAmount'));
          return;
        }
        const totalPaid = Number(formData.amount || 0);
        if (!(totalPaid > 0)) {
          error(t('contributionsPage.form.autoAllocateInvalidTotal'));
          return;
        }
        const targetPersonId = String(submitData.person_id || '').trim();
        const allRes = await contributionsAPI.getAll({
          limit: ALL_ITEMS_LIMIT,
          skip: 0,
          person_id: targetPersonId,
          member_id: targetPersonId,
        });
        const memberRows = Array.isArray(allRes?.data)
          ? allRes.data
          : (allRes?.data?.contributions || []);
        // IMPORTANT: le point de départ est le dernier mois payé DU MEMBRE CONCERNE.
        // Ici, on se base uniquement sur les lignes retournées par l'API filtrée par person/member_id.
        const paidRegularMonths = memberRows
          .filter((c) =>
            isRegularMonthKey(c?.month) &&
            ['completed', 'approved', 'paid'].includes(String(c?.status || '').toLowerCase())
          )
          .map((c) => String(c.month).trim());
        const lastPaidMonth = paidRegularMonths.sort((a, b) => monthSortRank(b) - monthSortRank(a))[0] || '';
        const fallbackStartMonth = isRegularMonthKey(formData.month)
          ? String(formData.month).trim()
          : getLocalDateISO().slice(0, 7);
        const startMonth = lastPaidMonth ? nextMonthKey(lastPaidMonth) : fallbackStartMonth;
        if (!isRegularMonthKey(startMonth)) {
          error(t('contributionsPage.form.autoAllocateInvalidStartMonth'));
          return;
        }
        const splits = buildMonthlySplits(totalPaid, expectedMonthlyAmount, startMonth);
        if (!splits.length) {
          error(t('contributionsPage.form.autoAllocateInvalidTotal'));
          return;
        }
        const existingMonths = new Set(memberRows.map((c) => String(c?.month || '').trim()));
        const duplicateGenerated = splits.find((row) => existingMonths.has(row.month));
        if (duplicateGenerated) {
          error(
            t('contributionsPage.form.autoAllocateDuplicate', {
              month: getMonthName(duplicateGenerated.month),
            })
          );
          return;
        }
        const payloadRows = splits.map((row) => ({
          ...submitData,
          month: row.month,
          amount: row.amount,
        }));
        let createdRows = [];
        if (payloadRows.length === 1) {
          const res = await contributionsAPI.create(payloadRows[0]);
          createdRows = [res?.data || { ...payloadRows[0], id: res?.data?.id }];
        } else {
          const res = await contributionsAPI.bulkCreate(payloadRows);
          const createdIds = Array.isArray(res?.data?.created_ids) ? res.data.created_ids : [];
          createdRows = payloadRows.map((row, idx) => ({
            ...row,
            id: createdIds[idx] || undefined,
          }));
        }
        success(t('contributionsPage.success.bulkCreated', { count: payloadRows.length }));
        openContributionReceipt(createdRows, getMemberName(String(submitData.person_id || '').trim()));
      } else if (selectedContribution) {
        await contributionsAPI.update(selectedContribution.id, submitData);
        success(t('contributionsPage.success.update'));
      } else {
        const res = await contributionsAPI.create(submitData);
        const created = res?.data && typeof res.data === 'object'
          ? res.data
          : { ...submitData, id: res?.data?.id };
        success(
          isMembre ? t('contributionsPage.success.createMember') : t('contributionsPage.success.createAdmin')
        );
        openContributionReceipt(created, getMemberName(String(created.person_id || created.member_id || submitData.person_id || '').trim()));
      }
      
      loadData();
      loadAdminContributionStats();
      setShowForm(false);
      resetForm();
    } catch (err) {
      let errorMessage = t('contributionsPage.errors.saveGeneric');
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Erreur de validation Pydantic (liste d'erreurs)
          const details = err.response.data.detail
            .map((d) =>
              t('contributionsPage.errors.validationField', {
                field: d.loc?.join('.') || t('contributionsPage.errors.fieldWord'),
                msg: d.msg || d.message || t('contributionsPage.errors.validationFallback'),
              })
            )
            .join(', ');
          errorMessage = `${t('contributionsPage.errors.validationPrefix')}: ${details}`;
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = JSON.stringify(err.response.data.detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      error(errorMessage);
      console.error('Erreur lors de la sauvegarde:', err);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier que des membres sont sélectionnés
    if (!bulkData.member_ids || bulkData.member_ids.length === 0) {
      error(t('contributionsPage.errors.selectMember'));
      return;
    }
    
    if (bulkFormKind === 'exceptional') {
      if (!parseExceptionalTypeKeyFromMonth(bulkData.month)) {
        error(t('contributionsPage.errors.exceptionalTypeRequired'));
        return;
      }
    } else if (!bulkData.month) {
      error(t('contributionsPage.errors.selectMonth'));
      return;
    }

    const bulkMonthKey =
      bulkFormKind === 'exceptional'
        ? String(bulkData.month || '').trim()
        : String(bulkData.month || '').trim().slice(0, 7);
    if (bulkMonthKey) {
      const dupMember = (bulkData.member_ids || []).find((memberId) =>
        (contributions || []).some((c) => {
          const sameMember = String(getContributionMemberId(c) || '').trim() === String(memberId || '').trim();
          const cMonth = String(c?.month || '').trim();
          const sameMonth =
            bulkFormKind === 'exceptional' ? cMonth === bulkMonthKey : cMonth.slice(0, 7) === bulkMonthKey;
          return sameMember && sameMonth;
        })
      );
      if (dupMember) {
        const label =
          bulkFormKind === 'exceptional' ? getMonthName(bulkData.month) : bulkData.month;
        error(
          t('contributionsPage.errors.duplicateMemberMonth', {
            member: getMemberName(dupMember),
            label,
          })
        );
        return;
      }
    }

    if (!ledgerPmLoading && paymentMethods.length === 0) {
      setShowLedgerSetupModal(true);
      return;
    }
    if (ledgerPmLoading && paymentMethods.length === 0) {
      error(t('contributionsPage.errors.pmLoading'));
      return;
    }
    if (!bulkData.payment_method || String(bulkData.payment_method).trim() === '') {
      error(t('contributionsPage.errors.paymentMethodRequired'));
      return;
    }

    try {
      // Transformer bulkData en liste de ContributionCreate
      const exceptionalMonthNorm =
        bulkFormKind === 'exceptional' && parseExceptionalTypeKeyFromMonth(bulkData.month)
          ? formatExceptionalMonthKey(parseExceptionalTypeKeyFromMonth(bulkData.month))
          : bulkData.month;
      const paymentDateResolved = String(bulkData.payment_date || '').trim() || getLocalDateISO();
      const contributionsData = bulkData.member_ids.map((memberId) => ({
        person_id: memberId,
        month: exceptionalMonthNorm,
        amount: bulkData.amount || 5000,
        status: 'pending', // Par défaut en attente pour les créations en masse
        payment_method: bulkData.payment_method,
        payment_date: paymentDateResolved,
        notes: null,
        proof_documents: null
      }));
      
      const response = await contributionsAPI.bulkCreate(contributionsData);
      
      // Afficher les résultats
      if (response.data.created_count > 0) {
        success(t('contributionsPage.success.bulkCreated', { count: response.data.created_count }));
      }
      
      if (response.data.error_count > 0) {
        // Formater les erreurs pour l'affichage
        let errorMessage = t('contributionsPage.errors.bulkErrorCount', { count: response.data.error_count });
        if (response.data.errors && response.data.errors.length > 0) {
          const errorDetails = response.data.errors
            .map(e => {
              if (typeof e.error === 'string') {
                return e.error;
              } else if (typeof e.error === 'object') {
                return JSON.stringify(e.error);
              }
              return t('contributionsPage.errors.unknownLine');
            })
            .join(', ');
          errorMessage += t('contributionsPage.errors.bulkDetailsSuffix', { details: errorDetails });
        }
        error(errorMessage);
      }
      
      loadData();
      loadAdminContributionStats();
      setShowBulkForm(false);
      resetBulkForm();
    } catch (err) {
      // Gérer les erreurs de validation Pydantic
      let errorMessage = t('contributionsPage.errors.bulkCreateGeneric');
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Erreur de validation Pydantic (liste d'erreurs)
          const details = err.response.data.detail
            .map((d) =>
              t('contributionsPage.errors.validationField', {
                field: d.loc?.join('.') || t('contributionsPage.errors.fieldWord'),
                msg: d.msg || d.message || t('contributionsPage.errors.validationFallback'),
              })
            )
            .join(', ');
          errorMessage = `${t('contributionsPage.errors.validationPrefix')}: ${details}`;
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = JSON.stringify(err.response.data.detail);
        }
      }
      error(errorMessage);
      console.error('Bulk create error:', err);
    }
  };

  const handleImportContributionsFile = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!ledgerPmLoading && paymentMethods.length === 0) {
      setShowLedgerSetupModal(true);
      return;
    }
    try {
      setImportingContributions(true);
      const rows = await parseTabularFile(file);
      if (!Array.isArray(rows) || rows.length === 0) {
        error(t('contributionsPage.errors.importEmpty'));
        return;
      }
      const { data } = await adminAPI.importData('contributions', rows, 'json', { strict_ledger: true });
      const created = Number(data?.created_count || 0);
      const errorsList = Array.isArray(data?.errors) ? data.errors : [];
      const warningsList = Array.isArray(data?.warnings) ? data.warnings : [];
      const errorsCount = errorsList.length;
      const warningsCount = warningsList.length;
      const logContent = buildContributionsImportLogContent(
        {
          created,
          errors: errorsList,
          warnings: warningsList,
        },
        t
      );
      const logCsv = buildContributionsImportLogCsv(
        {
          created,
          errors: errorsList,
          warnings: warningsList,
        },
        t
      );
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      downloadTextFile(
        `cotisations_import_log_${stamp}.txt`,
        logContent,
        'text/plain;charset=utf-8'
      );
      downloadTextFile(
        `cotisations_import_log_${stamp}.csv`,
        logCsv,
        'text/csv;charset=utf-8'
      );
      if (errorsCount > 0) {
        const firstError = errorsList[0]?.error || t('contributionsPage.errors.importDetailError');
        warning(
          t('contributionsPage.success.importWithErrors', {
            created,
            errorsCount,
            firstError,
          })
        );
      } else if (warningsCount > 0) {
        const firstWarning = warningsList[0]?.warning || t('contributionsPage.errors.importDetailWarning');
        warning(
          t('contributionsPage.success.importWithWarnings', {
            created,
            warningsCount,
            firstWarning,
          })
        );
      } else {
        success(t('contributionsPage.success.importDone', { count: created }));
      }
      await loadData();
      loadAdminContributionStats();
    } catch (err) {
      error(err?.response?.data?.detail || t('contributionsPage.errors.importGeneric'));
    } finally {
      setImportingContributions(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: t('contributionsPage.confirm.deleteTitle'),
      message: t('contributionsPage.confirm.deleteMessage'),
      confirmText: t('contributionsPage.confirm.delete'),
      cancelText: t('contributionsPage.confirm.cancel'),
      type: 'danger',
    });
    
    if (!confirmed) return;
    
    try {
      await contributionsAPI.delete(id);
      // Message « demande en attente d’approbation » : profils type trésorier (delete sans dashboard.stats org).
      if (useTreasurerDeletionSuccessCopy) {
        success(t('contributionsPage.success.deletePending'));
      } else {
        success(t('contributionsPage.success.delete'));
      }
      loadData();
      loadAdminContributionStats();
    } catch (err) {
      error(t('contributionsPage.errors.delete'));
    }
  };

  const runApproveContribution = async (contrib) => {
    return contributionsAPI.approve(contrib.id);
  };

  const handleApprove = async (contrib) => {
    try {
      const res = await runApproveContribution(contrib);
      if (res?.data?.ledger_pending_applied === false) {
        warning(t('contributionsPage.warnings.approveNoLedger'));
      } else {
        success(t('contributionsPage.success.approve'));
      }
      loadData();
      loadAdminContributionStats();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      error(typeof detail === 'string' ? detail : t('contributionsPage.errors.approve'));
    }
  };

  const handleSelectAllContributions = (e) => {
    if (e.target.checked) {
      setSelectedContributionIds(filteredContributions.map((c) => c.id));
    } else {
      setSelectedContributionIds([]);
    }
  };

  const toggleContributionSelection = (id) => {
    setSelectedContributionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkApproveContributions = async () => {
    const pending = filteredContributions.filter(
      (c) => selectedContributionIds.includes(c.id) && c.status === 'pending'
    );
    if (pending.length === 0) {
      error(t('contributionsPage.errors.noPendingSelection'));
      return;
    }
    const ok = await confirm({
      title: t('contributionsPage.confirm.bulkApproveTitle'),
      message: t('contributionsPage.confirm.bulkApproveMessage', { count: pending.length }),
      confirmText: t('contributionsPage.confirm.approve'),
      cancelText: t('contributionsPage.confirm.cancel'),
      type: 'info',
    });
    if (!ok) return;
    try {
      let ledgerMissing = 0;
      for (const c of pending) {
        const res = await runApproveContribution(c);
        if (res?.data?.ledger_pending_applied === false) ledgerMissing += 1;
      }
      if (ledgerMissing > 0) {
        warning(
          t('contributionsPage.warnings.approveBulkPartial', {
            count: pending.length,
            missing: ledgerMissing,
          })
        );
      } else {
        success(t('contributionsPage.success.approveBulk', { count: pending.length }));
      }
      setSelectedContributionIds([]);
      loadData();
      loadAdminContributionStats();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      error(typeof detail === 'string' ? detail : t('contributionsPage.errors.approveBulk'));
    }
  };

  const handleBulkDeleteContributions = async () => {
    const ids = selectedContributionIds.filter((id) =>
      filteredContributions.some((c) => c.id === id)
    );
    if (ids.length === 0) return;
    const ok = await confirm({
      title: t('contributionsPage.confirm.bulkDeleteTitle'),
      message: t('contributionsPage.confirm.bulkDeleteMessage', { count: ids.length }),
      confirmText: t('contributionsPage.confirm.delete'),
      cancelText: t('contributionsPage.confirm.cancel'),
      type: 'danger',
    });
    if (!ok) return;
    try {
      for (const id of ids) {
        await contributionsAPI.delete(id);
      }
      success(t('contributionsPage.success.bulkDelete', { count: ids.length }));
      setSelectedContributionIds([]);
      loadData();
      loadAdminContributionStats();
    } catch (err) {
      error(t('contributionsPage.errors.bulkDelete'));
    }
  };

  const handleFileUpload = async (event) => {
    const inputEl = event?.target || event?.currentTarget || null;
    const files = Array.from(inputEl?.files || []);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const response = await contributionsAPI.uploadFiles(files);
      const payload = response?.data || {};
      const uploadedUrls = Array.isArray(payload?.files)
        ? payload.files
        : Array.isArray(payload)
          ? payload
          : [];
      if (uploadedUrls.length === 0) {
        throw new Error(t('contributionsPage.errors.noServerFile'));
      }
      
      // Créer des objets avec description vide pour chaque fichier uploadé
      const newProofDocuments = uploadedUrls.map(fileUrl => ({
        file_url: fileUrl,
        description: '' // L'utilisateur devra ajouter une description
      }));
      
      setUploadedFiles([...uploadedFiles, ...newProofDocuments]);
      setFormData({
        ...formData,
        proof_documents: [...(formData.proof_documents || []), ...newProofDocuments]
      });
      success(t('contributionsPage.success.upload', { count: uploadedUrls.length }));
    } catch (err) {
      let errorMessage = t('contributionsPage.errors.uploadGeneric');
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Erreur de validation Pydantic (liste d'erreurs)
          const details = err.response.data.detail
            .map((d) =>
              t('contributionsPage.errors.validationField', {
                field: d.loc?.join('.') || t('contributionsPage.errors.fieldWord'),
                msg: d.msg || d.message || t('contributionsPage.errors.validationFallback'),
              })
            )
            .join(', ');
          errorMessage = `${t('contributionsPage.errors.validationPrefix')}: ${details}`;
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = JSON.stringify(err.response.data.detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      error(errorMessage);
      console.error('Erreur lors de l\'upload:', err);
    } finally {
      setUploading(false);
      if (inputEl) {
        inputEl.value = '';
      }
    }
  };

  const removeFile = (fileUrlToRemove) => {
    // Filtrer les fichiers de uploadedFiles
    const newFiles = uploadedFiles.filter(f => {
      const currentFileUrl = typeof f === 'string' ? f : f.file_url;
      return currentFileUrl !== fileUrlToRemove;
    });
    
    // Mettre à jour uploadedFiles (source de vérité)
    setUploadedFiles(newFiles);
    
    // Synchroniser formData.proof_documents avec uploadedFiles
    setFormData(prev => ({
      ...prev,
      proof_documents: newFiles
    }));
  };

  const updateFileDescription = (fileUrl, description) => {
    const updatedFiles = uploadedFiles.map(f => {
      if (typeof f === 'string') {
        // Ancien format (string), convertir en objet
        return f === fileUrl ? { file_url: f, description } : { file_url: f, description: '' };
      } else {
        return f.file_url === fileUrl ? { ...f, description } : f;
      }
    });
    setUploadedFiles(updatedFiles);
    
    const updatedProofDocs = formData.proof_documents?.map(f => {
      if (typeof f === 'string') {
        return f === fileUrl ? { file_url: f, description } : { file_url: f, description: '' };
      } else {
        return f.file_url === fileUrl ? { ...f, description } : f;
      }
    }) || [];
    
    setFormData({
      ...formData,
      proof_documents: updatedProofDocs
    });
  };

  const resetForm = () => {
    setFormKind('monthly');
    setPaymentForWhom(null);
    setFormData({
      member_id: '',
      person_id: '',
      amount: 5000,
      month: '',
      status: 'pending',
      payment_method: defaultPaymentMethod,
      notes: '',
      proof_documents: []
    });
    setSelectedContribution(null);
    setUploadedFiles([]);
  };

  /** Ouvre le formulaire d’enregistrement (choix du bénéficiaire si le membre peut payer pour autrui). */
  const openCreatePaymentForm = () => {
    resetForm();
    setFormKind('monthly');
    setSelectedContribution(null);
    setUploadedFiles([]);
    if (canMemberPayForOthers) {
      setShowPayForWhomModal(true);
      return;
    }
    setPaymentForWhom('self');
    setFormData((prev) => ({
      ...prev,
      member_id: effectivePersonId,
      person_id: effectivePersonId,
      status: 'pending',
      payment_method: defaultPaymentMethod,
    }));
    setShowForm(true);
  };

  const startPaymentForSelf = () => {
    setPaymentForWhom('self');
    setShowPayForWhomModal(false);
    setFormData((prev) => ({
      ...prev,
      member_id: effectivePersonId,
      person_id: effectivePersonId,
      status: 'pending',
      payment_method: defaultPaymentMethod,
      amount: Number(settings?.financial?.contribution_amount || prev.amount || 5000),
    }));
    setShowForm(true);
  };

  const startPaymentForOther = () => {
    setPaymentForWhom('other');
    setShowPayForWhomModal(false);
    setFormData((prev) => ({
      ...prev,
      member_id: '',
      person_id: '',
      status: 'completed',
      payment_method: defaultPaymentMethod,
      amount: Number(settings?.financial?.contribution_amount || prev.amount || 5000),
    }));
    setShowForm(true);
  };

  const resetBulkForm = () => {
    setBulkFormKind('monthly');
    setBulkData({
      member_ids: [],
      month: '',
      amount: 5000,
      payment_method: defaultPaymentMethod,
      payment_date: getLocalDateISO()
    });
  };

  const getMemberName = (memberId) => {
    if (!Array.isArray(members)) return t('contributionsPage.unknownMember');
    const normalizedMemberId = String(memberId || '').trim();
    const member = members.find((m) => String(m?.id || '').trim() === normalizedMemberId);
    if (member?.name) return member.name;
    // Pour les membres, utiliser le nom de l'utilisateur connecté si c'est leur propre ID
    if (isMembre && String(user?.member_id || '').trim() === normalizedMemberId && user?.name) {
      return user.name;
    }
    return t('contributionsPage.unknownMember');
  };

  const getMemberPhone = (memberId) => {
    const normalizedMemberId = String(memberId || '').trim();
    if (!normalizedMemberId) {
      return String(user?.phone || '').trim();
    }
    const member = Array.isArray(members)
      ? members.find((m) => String(m?.id || '').trim() === normalizedMemberId)
      : null;
    const fromMember = String(member?.phone || member?.mobile || member?.whatsapp || '').trim();
    if (fromMember) return fromMember;
    const fromUserIndex = Object.values(usersById || {}).find((u) => {
      const pid = String(u?.person_id || u?.member_id || '').trim();
      return pid && pid === normalizedMemberId;
    });
    const fromUser = String(fromUserIndex?.phone || '').trim();
    if (fromUser) return fromUser;
    if (
      isMembre &&
      (String(user?.member_id || '').trim() === normalizedMemberId ||
        String(user?.person_id || '').trim() === normalizedMemberId)
    ) {
      return String(user?.phone || '').trim();
    }
    return '';
  };

  const getMonthName = (month) => {
    if (!month) return '-';
    const exKey = parseExceptionalTypeKeyFromMonth(month);
    if (exKey) {
      const exEntry = exceptionalTypes.find((x) => x.key === exKey);
      if (exEntry) return exEntry.label;
      return exKey;
    }
    // Afficher uniquement mois + année (ignore le jour éventuel d’un ISO).
    const ym = String(month).trim().slice(0, 7);
    const parts = ym.split('-');
    if (parts.length < 2) return String(month);
    const year = Number(parts[0]);
    const monthNum = Number(parts[1]);
    if (!Number.isFinite(year) || !Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) {
      return String(month);
    }
    const date = new Date(year, monthNum - 1, 1);
    if (Number.isNaN(date.getTime())) return String(month);
    return date.toLocaleDateString(localeFmt, { month: 'long', year: 'numeric' });
  };

  const getContributionStatusLabel = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'completed' || s === 'approved' || s === 'paid') {
      return t('contributionsPage.table.statusPaidFull');
    }
    if (s === 'rejected') return t('contributionsPage.table.statusRejectedFull');
    return t('contributionsPage.table.statusPendingFull');
  };

  const getContributionMemberId = (contrib) => {
    const createdByUser = usersById[String(contrib?.created_by || '').trim()];
    const linkedPersonId = createdByUser?.person_id || createdByUser?.member_id || '';
    return String(
      contrib?.person_id ||
      contrib?.member_id ||
      contrib?.person?.id ||
      contrib?.member?.id ||
      contrib?.metadata?.person_id ||
      contrib?.metadata?.member_id ||
      linkedPersonId ||
      ''
    ).trim();
  };

  /** Normalise person_id pour le calcul d’impayés (aligné sur getContributionMemberId). */
  const contributionsScopedToPerson = useCallback(
    (rows, personId) => {
      const pid = String(personId || '').trim();
      if (!pid) return [];
      return (Array.isArray(rows) ? rows : [])
        .filter((c) => getContributionMemberId(c) === pid)
        .map((c) => ({
          ...c,
          person_id: pid,
          member_id: c?.member_id || pid,
        }));
    },
    [usersById]
  );

  const openContributionReceipt = (contribs, memberNameOverride, memberPhoneOverride) => {
    const rows = (Array.isArray(contribs) ? contribs : [contribs])
      .filter(Boolean)
      .map((c) => ({
        id: c?.id,
        month: c?.month,
        periodLabel: getMonthName(c?.month),
        amount: c?.amount,
        payment_method: c?.payment_method,
        status: c?.status,
        statusLabel: getContributionStatusLabel(c?.status),
        notes: c?.notes,
        person_id: getContributionMemberId(c),
      }));
    if (!rows.length) return;
    const memberId = rows[0].person_id;
    setReceiptData({
      memberName: memberNameOverride || getMemberName(memberId),
      memberPhone: memberPhoneOverride || getMemberPhone(memberId),
      personId: memberId,
      contributions: rows,
      issuedAt: new Date().toLocaleString(localeFmt, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    });
  };

  const formatContributionDueDate = (contrib) => {
    const billing = settings?.financial?.contribution_billing || {};
    const day = Math.min(28, Math.max(1, Number(billing.payment_deadline_day) || 15));
    const monthKey = String(contrib?.month || '').trim().slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(monthKey)) {
      const [y, m] = monthKey.split('-').map(Number);
      const due = new Date(y, m - 1, day);
      return due.toLocaleDateString(localeFmt, { dateStyle: 'medium' });
    }
    const now = new Date();
    const due = new Date(now.getFullYear(), now.getMonth(), day);
    if (due.getTime() < now.getTime()) {
      due.setMonth(due.getMonth() + 1);
    }
    return due.toLocaleDateString(localeFmt, { dateStyle: 'medium' });
  };

  const openContributionInvoice = (contribs, memberNameOverride, memberPhoneOverride) => {
    const source = (Array.isArray(contribs) ? contribs : [contribs]).filter(Boolean);
    const unpaid = source.filter((c) => isUnpaidContributionStatus(c?.status));
    const rows = unpaid.map((c) => ({
      id: c?.id,
      month: c?.month,
      periodLabel: getMonthName(c?.month),
      amount: c?.amount,
      payment_method: c?.payment_method,
      status: c?.status,
      statusLabel: getContributionStatusLabel(c?.status) || t('contributionsPage.invoice.statusUnpaid'),
      notes: c?.notes,
      person_id: getContributionMemberId(c) || c?.person_id,
    }));
    if (!rows.length) {
      error(t('contributionsPage.invoice.bulkNoneUnpaid'));
      return;
    }
    const memberIds = [...new Set(rows.map((r) => String(r.person_id || '').trim()).filter(Boolean))];
    if (memberIds.length > 1) {
      error(t('contributionsPage.invoice.bulkSameMember'));
      return;
    }
    const memberId = memberIds[0] || '';
    const latestUnpaid = [...unpaid].sort((a, b) =>
      String(a?.month || '').localeCompare(String(b?.month || ''))
    ).at(-1);
    setInvoiceData({
      memberName: memberNameOverride || getMemberName(memberId),
      memberPhone: memberPhoneOverride || getMemberPhone(memberId),
      contributions: rows,
      issuedAt: new Date().toLocaleString(localeFmt, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
      dueAt: formatContributionDueDate(latestUnpaid),
    });
  };

  /** Facture construite depuis les mois impayés (traînant = badge « Mois impayés »). */
  const openArrearsInvoiceForMember = async (personIdOverride = null) => {
    const personId = String(
      personIdOverride ||
        (isMembre ? effectivePersonId : selectedMember !== 'all' ? selectedMember : '') ||
        ''
    ).trim();
    if (!personId) {
      error(t('contributionsPage.invoice.pickMember'));
      return;
    }
    const member = Array.isArray(members)
      ? members.find((m) => String(m?.id || '').trim() === personId)
      : null;
    if (member && !memberIsActiveForArrearsInvoice(member)) {
      error(t('contributionsPage.invoice.memberInactive'));
      return;
    }

    let sourceRows = contributionsScopedToPerson(
      isMembre ? memberAllContributions || contributions || [] : adminAllContributions || contributions || [],
      personId
    );

    // Historique complet du membre (évite une facture gonflée si la liste filtrée/paginée est incomplète).
    try {
      const res = await contributionsAPI.getAll({
        limit: ALL_ITEMS_LIMIT,
        skip: 0,
        person_id: personId,
        member_id: personId,
      });
      const fetched = Array.isArray(res?.data) ? res.data : res?.data?.contributions || [];
      if (Array.isArray(fetched) && fetched.length) {
        sourceRows = contributionsScopedToPerson(fetched, personId);
      }
    } catch (_e) {
      // garder sourceRows déjà en mémoire
    }

    const monthlyAmount = Number(settings?.financial?.contribution_amount || 0);
    const joinMonth = member?.join_date || member?.membership_date || member?.created_at || '';
    let targetCount = null;
    try {
      const stRes = await membersAPI.getStats(personId);
      const n = Number(stRes?.data?.unpaid_months);
      if (Number.isFinite(n) && n >= 0) targetCount = n;
    } catch (_e) {
      /* ignore */
    }
    const monthKeys = listUnpaidRegularMonthKeys(sourceRows, {
      personId,
      joinMonth,
      maxLookback: 36,
      targetCount,
      paymentDeadlineDay,
    });
    const built = buildArrearsInvoiceContributionRows({
      contributions: sourceRows,
      personId,
      monthlyAmount,
      monthKeys,
      joinMonth,
    });
    if (!built.length) {
      error(t('contributionsPage.invoice.noArrears'));
      return;
    }
    if (built.every((r) => !(Number(r.amount) > 0))) {
      error(t('contributionsPage.invoice.missingAmount'));
      return;
    }
    openContributionInvoice(built, getMemberName(personId), getMemberPhone(personId));
  };

  const handleBulkInvoiceContributions = () => {
    const selected = filteredContributions.filter((c) =>
      selectedContributionIds.includes(c?.id)
    );
    const unpaidSelected = selected.filter((c) => isUnpaidContributionStatus(c?.status));
    if (unpaidSelected.length) {
      openContributionInvoice(unpaidSelected);
      return;
    }
    const memberIds = [
      ...new Set(selected.map((c) => getContributionMemberId(c)).filter(Boolean)),
    ];
    if (memberIds.length === 1) {
      openArrearsInvoiceForMember(memberIds[0]);
      return;
    }
    if (selectedMember !== 'all') {
      openArrearsInvoiceForMember(selectedMember);
      return;
    }
    error(t('contributionsPage.invoice.bulkNoneUnpaid'));
  };

  const getFormPersonId = () => String(formData?.person_id || formData?.member_id || '').trim();
  useEffect(() => {
    // Toujours le membre du formulaire (y compris « pour un autre » côté président / bureau).
    const targetPersonId = String(
      formData?.person_id || formData?.member_id || (isMembre ? effectivePersonId : '') || ''
    ).trim();
    if (!showForm || !targetPersonId) {
      setSelectedMemberLastPaidMonth('');
      setSelectedMemberLastPaidLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setSelectedMemberLastPaidLoading(true);
      try {
        const res = await contributionsAPI.getAll({
          limit: ALL_ITEMS_LIMIT,
          skip: 0,
          person_id: targetPersonId,
          member_id: targetPersonId,
        });
        const rows = Array.isArray(res?.data) ? res.data : (res?.data?.contributions || []);
        const paidRegularMonths = (Array.isArray(rows) ? rows : [])
          .filter((c) =>
            isRegularMonthKey(c?.month) &&
            ['completed', 'approved', 'paid'].includes(String(c?.status || '').toLowerCase())
          )
          .map((c) => String(c.month).trim());
        const lastPaidMonth = paidRegularMonths.sort((a, b) => monthSortRank(b) - monthSortRank(a))[0] || '';
        if (!cancelled) setSelectedMemberLastPaidMonth(lastPaidMonth);
      } catch (_e) {
        if (!cancelled) setSelectedMemberLastPaidMonth('');
      } finally {
        if (!cancelled) setSelectedMemberLastPaidLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showForm, isMembre, effectivePersonId, formData?.person_id, formData?.member_id]);

  const monthlyContributionAmount = Number(settings?.financial?.contribution_amount || 0);
  /** Répartition auto si création mensuelle sans mois choisi (plus de case à cocher). */
  const shouldAutoAllocateMonthly = useMemo(() => {
    if (selectedContribution || formKind !== 'monthly') return false;
    return !isRegularMonthKey(String(formData.month || '').trim().slice(0, 7));
  }, [selectedContribution, formKind, formData.month]);

  const autoAllocatePreview = useMemo(() => {
    const enabled = shouldAutoAllocateMonthly;
    if (!enabled) {
      return { enabled: false, rows: [], total: 0, monthly: monthlyContributionAmount, startMonth: '' };
    }
    const targetPersonId = String(getFormPersonId() || effectivePersonId || '').trim();
    const fallbackStartMonth = isRegularMonthKey(formData.month)
      ? String(formData.month).trim()
      : getLocalDateISO().slice(0, 7);
    if (!(monthlyContributionAmount > 0)) {
      return {
        enabled: true,
        rows: [],
        total: Number(formData.amount || 0),
        monthly: monthlyContributionAmount,
        startMonth: fallbackStartMonth,
        error: 'monthly_not_configured',
      };
    }
    if (!(Number(formData.amount || 0) > 0)) {
      return {
        enabled: true,
        rows: [],
        total: Number(formData.amount || 0),
        monthly: monthlyContributionAmount,
        startMonth: fallbackStartMonth,
        error: 'invalid_total',
      };
    }
    // Toujours aligner l'aperçu sur le dernier mois payé calculé pour le membre sélectionné.
    const lastPaidMonth = String(selectedMemberLastPaidMonth || '').trim();
    const startMonth = lastPaidMonth ? nextMonthKey(lastPaidMonth) : fallbackStartMonth;
    const rows = buildMonthlySplits(Number(formData.amount || 0), monthlyContributionAmount, startMonth);
    return {
      enabled: true,
      rows,
      total: Number(formData.amount || 0),
      monthly: monthlyContributionAmount,
      startMonth,
      lastPaidMonth,
    };
  }, [
    shouldAutoAllocateMonthly,
    monthlyContributionAmount,
    formData.amount,
    formData.month,
    formData.person_id,
    formData.member_id,
    effectivePersonId,
    selectedMemberLastPaidMonth,
  ]);

  const getContributionMemberName = (contrib) => {
    const createdByUser = usersById[String(contrib?.created_by || '').trim()];
    const directName = String(
      contrib?.member_name ||
      contrib?.person_name ||
      contrib?.submitted_by_name ||
      contrib?.member?.name ||
      contrib?.person?.name ||
      createdByUser?.name ||
      createdByUser?.full_name ||
      createdByUser?.display_name ||
      createdByUser?.email ||
      createdByUser?.username ||
      ''
    ).trim();
    if (directName) return directName;
    return getMemberName(getContributionMemberId(contrib));
  };

  const contributionBilling = settings?.financial?.contribution_billing || {};
  const paymentDeadlineDay = Math.min(28, Math.max(1, Number(contributionBilling.payment_deadline_day) || 15));
  const arrearsMin = Math.min(12, Math.max(1, Number(contributionBilling.arrears_warning_min_months) || 3));
  const arrearsMax = Math.min(12, Math.max(1, Number(contributionBilling.arrears_warning_max_months) || 4));
  const arrearsBandLo = Math.min(arrearsMin, arrearsMax);
  const arrearsBandHi = Math.max(arrearsMin, arrearsMax);
  const arrearsNoticeDays = Math.max(1, Number(contributionBilling.arrears_notice_period_days) || 15);
  const arrearsDeactivationDays = Math.max(1, Number(contributionBilling.arrears_deactivation_notice_days) || 15);

  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const paidStatusSet = useMemo(() => new Set(['completed', 'paid', 'approved']), []);

  const isPaidContributionStatus = useCallback((status) => {
    return paidStatusSet.has(String(status || '').toLowerCase().trim());
  }, [paidStatusSet]);

  const contributionMonthKey = useCallback((c) => {
    const m = String(c?.month || '').trim();
    if (!m || isAdhesionContributionMonth(m) || isExceptionalMonthValue(m)) return '';
    if (!/^\d{4}-\d{2}/.test(m)) return '';
    return m.slice(0, 7);
  }, []);

  /** Historique membre : fusion liste paginée + historique complet (évite un bandeau faux). */
  const memberHistoryRowsForNotices = useMemo(() => {
    if (!isMembre) return [];
    const byId = new Map();
    for (const c of [...(memberAllContributions || []), ...(contributions || [])]) {
      if (!c) continue;
      const id = String(c?.id || '').trim();
      const key = id || `${contributionMonthKey(c)}:${c?.status}:${c?.amount}`;
      if (!byId.has(key)) byId.set(key, c);
    }
    return [...byId.values()];
  }, [isMembre, memberAllContributions, contributions, contributionMonthKey]);

  const memberPaymentDeadline = useMemo(() => {
    if (!isMembre) return null;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const startToday = new Date(y, m, d);
    let deadline = new Date(y, m, paymentDeadlineDay);
    if (startToday.getTime() > deadline.getTime()) {
      deadline = new Date(y, m + 1, paymentDeadlineDay);
    }
    const daysLeft = Math.max(0, Math.round((deadline - startToday) / 86400000));
    return { daysLeft, deadline, paymentDeadlineDay };
  }, [isMembre, paymentDeadlineDay]);

  const memberRowsForKpi = useMemo(() => {
    if (!isMembre || !effectivePersonId) return [];
    return (memberAllContributions || []).filter((c) => getContributionMemberId(c) === effectivePersonId);
  }, [isMembre, effectivePersonId, memberAllContributions, usersById]);

  const memberPaidMonthsForKpi = useMemo(() => {
    if (!isMembre) return 0;
    const api = Number(memberKpiStats?.paid_months);
    if (Number.isFinite(api) && api >= 0) return api;
    return countDistinctPaidRegularMonthsFromRows(memberRowsForKpi);
  }, [isMembre, memberKpiStats, memberRowsForKpi]);

  const memberUnpaidMonthsForKpi = useMemo(() => {
    if (!isMembre) return 0;
    const payload = buildMemberDashboardPersonalStats(
      String(effectivePersonId || ''),
      memberRowsForKpi,
      {
        paid_months: memberKpiStats?.paid_months,
        unpaid_months: memberKpiStats?.unpaid_months,
      },
      '',
      ''
    );
    return harmonizedMemberUnpaidMonthsDisplay(payload);
  }, [isMembre, effectivePersonId, memberRowsForKpi, memberKpiStats]);

  const arrearsSourceRows = useMemo(() => {
    if (isMembre) {
      return memberAllContributions?.length ? memberAllContributions : contributions || [];
    }
    if (
      selectedMember !== 'all' &&
      Array.isArray(adminSelectedMemberContributions) &&
      adminSelectedMemberContributions.length > 0
    ) {
      return adminSelectedMemberContributions;
    }
    return adminAllContributions?.length ? adminAllContributions : contributions || [];
  }, [
    isMembre,
    selectedMember,
    memberAllContributions,
    adminSelectedMemberContributions,
    adminAllContributions,
    contributions,
  ]);

  const unpaidTargetCountForPerson = useCallback(
    (personId) => {
      const pid = String(personId || '').trim();
      if (!pid) return null;
      if (isMembre && pid === String(effectivePersonId || '').trim()) {
        const n = Number(memberKpiStats?.unpaid_months);
        return Number.isFinite(n) && n >= 0 ? n : null;
      }
      if (!isMembre && pid === String(selectedMember || '').trim()) {
        const n = Number(adminSelectedMemberStats?.unpaid_months);
        return Number.isFinite(n) && n >= 0 ? n : null;
      }
      return null;
    },
    [isMembre, effectivePersonId, memberKpiStats, selectedMember, adminSelectedMemberStats]
  );

  const memberArrearsMonthKeys = useMemo(() => {
    if (!isMembre || !effectivePersonId) return [];
    const member = (members || []).find((m) => String(m?.id || '').trim() === String(effectivePersonId || '').trim());
    const scoped = contributionsScopedToPerson(arrearsSourceRows, effectivePersonId);
    const rows =
      scoped.length > 0
        ? scoped
        : memberHistoryRowsForNotices.map((c) => ({
            ...c,
            person_id: effectivePersonId,
          }));
    const paidMonths = rows
      .map((c) => (isPaidContributionStatus(c?.status) ? contributionMonthKey(c) : ''))
      .filter(Boolean)
      .sort();
    const firstPaidMonth = paidMonths[0] || '';
    const joinRaw = member?.join_date || member?.membership_date || member?.created_at || '';
    const joinYm = /^\d{4}-\d{2}/.test(String(joinRaw)) ? String(joinRaw).slice(0, 7) : '';
    const effectiveJoin = joinYm && (!firstPaidMonth || joinYm >= firstPaidMonth)
      ? joinYm
      : firstPaidMonth || joinYm || '';
    return listUnpaidRegularMonthKeys(rows, {
      personId: effectivePersonId,
      joinMonth: effectiveJoin,
      maxLookback: 36,
      targetCount: null,
      paymentDeadlineDay,
    });
  }, [
    isMembre,
    effectivePersonId,
    arrearsSourceRows,
    members,
    contributionsScopedToPerson,
    memberHistoryRowsForNotices,
    isPaidContributionStatus,
    contributionMonthKey,
    paymentDeadlineDay,
  ]);

  const adminArrearsMonthKeys = useMemo(() => {
    if (isMembre || selectedMember === 'all') return [];
    const member = (members || []).find((m) => String(m?.id || '').trim() === String(selectedMember || '').trim());
    return listUnpaidRegularMonthKeys(contributionsScopedToPerson(arrearsSourceRows, selectedMember), {
      personId: selectedMember,
      joinMonth: member?.join_date || member?.membership_date || member?.created_at || '',
      maxLookback: 36,
      targetCount: unpaidTargetCountForPerson(selectedMember),
      paymentDeadlineDay,
    });
  }, [
    isMembre,
    selectedMember,
    arrearsSourceRows,
    members,
    contributionsScopedToPerson,
    unpaidTargetCountForPerson,
    paymentDeadlineDay,
  ]);

  const canOfferArrearsInvoice = isMembre
    ? memberArrearsMonthKeys.length > 0
    : selectedMember !== 'all'
      ? adminArrearsMonthKeys.length > 0 &&
        memberIsActiveForArrearsInvoice(
          (members || []).find((m) => String(m?.id || '').trim() === String(selectedMember || '').trim())
        )
      : false;

  /** Lignes « dus / en retard » = mêmes mois que l’avis de cotisation en cours. */
  const dueOrOverduePaymentRows = useMemo(() => {
    const monthlyAmount = Number(settings?.financial?.contribution_amount || 0);
    const targetPersonIds = (() => {
      if (isMembre) {
        const id = String(effectivePersonId || '').trim();
        return id ? [id] : [];
      }
      if (selectedMember !== 'all') {
        const id = String(selectedMember || '').trim();
        return id ? [id] : [];
      }
      // Admin « tous » : membres actifs + tout person_id présent dans l’historique
      const ids = new Set();
      for (const m of Array.isArray(members) ? members : []) {
        if (!memberIsActiveForArrearsInvoice(m)) continue;
        const id = String(m?.id || '').trim();
        if (id) ids.add(id);
      }
      for (const c of Array.isArray(arrearsSourceRows) ? arrearsSourceRows : []) {
        const id = getContributionMemberId(c);
        if (!id) continue;
        const member = (members || []).find((m) => String(m?.id || '').trim() === id);
        if (member && !memberIsActiveForArrearsInvoice(member)) continue;
        ids.add(id);
      }
      return [...ids];
    })();

    const out = [];
    const seenKeys = new Set();
    for (const personId of targetPersonIds) {
      const member = (members || []).find((m) => String(m?.id || '').trim() === personId);
      if (member && !memberIsActiveForArrearsInvoice(member)) continue;
      let scoped = contributionsScopedToPerson(arrearsSourceRows, personId);
      // Vue membre : même source que le bandeau retard (évite KPI 0 FBU + avertissement 3 mois).
      if (isMembre && scoped.length === 0 && Array.isArray(arrearsSourceRows) && arrearsSourceRows.length) {
        scoped = arrearsSourceRows.map((c) => ({ ...c, person_id: personId }));
      }
      const joinMonth = member?.join_date || member?.membership_date || member?.created_at || '';
      const monthKeys = isMembre
        ? memberArrearsMonthKeys
        : listUnpaidRegularMonthKeys(scoped, {
            personId,
            joinMonth,
            maxLookback: 36,
            targetCount: unpaidTargetCountForPerson(personId),
            paymentDeadlineDay,
          });
      const built = buildArrearsInvoiceContributionRows({
        contributions: scoped,
        personId,
        monthlyAmount,
        monthKeys,
        joinMonth,
      });
      for (const row of built) {
        const key = `${personId}:${String(row.month || '').slice(0, 7)}`;
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        out.push({
          ...row,
          person_id: personId,
          memberName: member?.name || '',
        });
      }
    }

    // Filet de sécurité : toute cotisation non payée en base doit figurer dans le tableau
    const unpaidStatuses = new Set(['pending', 'unpaid', 'awaiting', 'draft']);
    for (const c of Array.isArray(arrearsSourceRows) ? arrearsSourceRows : []) {
      const st = String(c?.status || '').toLowerCase();
      if (!unpaidStatuses.has(st)) continue;
      const monthRaw = String(c?.month || '').trim();
      if (!monthRaw || isExceptionalMonthValue(monthRaw)) continue;
      if (!/^\d{4}-\d{2}/.test(monthRaw)) continue;
      const ym = monthRaw.slice(0, 7);
      const personId = getContributionMemberId(c);
      if (!personId) continue;
      if (isMembre && personId !== String(effectivePersonId || '').trim()) continue;
      if (!isMembre && selectedMember !== 'all' && personId !== String(selectedMember || '').trim()) continue;
      const member = (members || []).find((m) => String(m?.id || '').trim() === personId);
      if (member && !memberIsActiveForArrearsInvoice(member)) continue;
      const key = `${personId}:${ym}`;
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      out.push({
        id: c.id,
        month: ym,
        amount: Number(c.amount) || monthlyAmount || 0,
        payment_method: c.payment_method || '',
        status: c.status || 'unpaid',
        notes: c.notes || '',
        person_id: personId,
        memberName: member?.name || '',
        _virtual: false,
      });
    }

    return enrichDuePaymentRows(out, { deadlineDay: paymentDeadlineDay }).sort((a, b) => {
      const byMonth = String(b.month || '').localeCompare(String(a.month || ''));
      if (byMonth) return byMonth;
      return String(a.memberName || a.person_id || '').localeCompare(
        String(b.memberName || b.person_id || '')
      );
    });
  }, [
    isMembre,
    effectivePersonId,
    selectedMember,
    members,
    arrearsSourceRows,
    contributionsScopedToPerson,
    unpaidTargetCountForPerson,
    memberArrearsMonthKeys,
    settings?.financial?.contribution_amount,
    paymentDeadlineDay,
    usersById,
  ]);

  const duePaymentsTotalAmount = useMemo(
    () => dueOrOverduePaymentRows.reduce((sum, r) => sum + (Number(r?.amount) || 0), 0),
    [dueOrOverduePaymentRows]
  );

  /** Lignes dus/retard filtrées (mois / recherche), avant pagination. */
  const filteredDuePaymentRows = useMemo(() => {
    let rows = (dueOrOverduePaymentRows || []).map((r) => ({
      ...r,
      _duePayment: true,
    }));
    if (selectedMonth !== 'all') {
      const ym = String(selectedMonth || '').trim().slice(0, 7);
      rows = rows.filter((r) => String(r.month || '').trim().slice(0, 7) === ym);
    }
    if (!isMembre && searchQuery.trim()) {
      rows = rows.filter((r) =>
        searchFieldsMatch(searchQuery, [
          r.memberName,
          getMemberName(r.person_id),
          r.month,
          r.amount,
          r.notes,
          r.id,
        ])
      );
    }
    return sortContributions(rows, sortBy, sortOrder);
  }, [
    dueOrOverduePaymentRows,
    selectedMonth,
    searchQuery,
    isMembre,
    sortBy,
    sortOrder,
    usersById,
    members,
  ]);

  /** Tableau unique : historique paginé ; filtre « dus » paginé côté client. */
  const displayContributions = useMemo(() => {
    if (selectedStatus === 'due') {
      if (isAllItems) return filteredDuePaymentRows;
      const start = Math.max(0, (contributionsPage - 1) * effectiveItemsPerPage);
      return filteredDuePaymentRows.slice(start, start + effectiveItemsPerPage);
    }

    // « Tous » : uniquement la page API (les dus restent accessibles via le filtre dédié)
    return filteredContributions;
  }, [
    selectedStatus,
    filteredDuePaymentRows,
    filteredContributions,
    isAllItems,
    contributionsPage,
    effectiveItemsPerPage,
  ]);

  /** Total pour la pagination (API ou liste dus filtrée). */
  const listTotalItems =
    selectedStatus === 'due' ? filteredDuePaymentRows.length : contributionsTotal;

  const openRecordPaymentForDue = useCallback(
    (row) => {
      const personId = String(row?.person_id || '').trim();
      const month = String(row?.month || '').trim().slice(0, 7);
      const amount = Number(row?.amount) || Number(settings?.financial?.contribution_amount || 0) || 5000;
      if (!row?._virtual && row?.id && !String(row.id).startsWith('arrears:')) {
        const existing =
          (contributions || []).find((c) => String(c?.id) === String(row.id)) ||
          (memberAllContributions || []).find((c) => String(c?.id) === String(row.id));
        if (existing) {
          const formattedProofDocs = (existing.proof_documents || []).map((doc) => {
            if (typeof doc === 'string') return { file_url: doc, description: '' };
            return { file_url: doc.file_url || doc.url || '', description: doc.description || '' };
          });
          setSelectedContribution(existing);
          setFormKind('monthly');
          setFormData({
            member_id: getContributionMemberId(existing) || personId,
            person_id: getContributionMemberId(existing) || personId,
            amount: Number(existing.amount) || amount,
            month: existing.month || month,
            status: existing.status || 'pending',
            payment_method: existing.payment_method || defaultPaymentMethod,
            notes: existing.notes || '',
            proof_documents: formattedProofDocs,
          });
          setUploadedFiles(formattedProofDocs);
          setShowForm(true);
          return;
        }
      }
      setSelectedContribution(null);
      setFormKind('monthly');
      setPaymentForWhom(
        isMembre && personId && personId !== String(effectivePersonId || '').trim()
          ? 'other'
          : 'self'
      );
      setFormData({
        member_id: personId,
        person_id: personId,
        amount,
        month,
        status:
          isMembre && personId && personId !== String(effectivePersonId || '').trim()
            ? 'completed'
            : isMembre
              ? 'pending'
              : 'completed',
        payment_method: defaultPaymentMethod,
        notes: '',
        proof_documents: [],
      });
      setUploadedFiles([]);
      setShowForm(true);
    },
    [
      contributions,
      memberAllContributions,
      settings?.financial?.contribution_amount,
      defaultPaymentMethod,
      isMembre,
      effectivePersonId,
    ]
  );

  const arrearsInvoiceQueryHandledRef = useRef(false);
  useEffect(() => {
    if (loading || arrearsInvoiceQueryHandledRef.current) return;
    const memberQ = String(searchParams.get('member') || '').trim();
    const invoiceQ = String(searchParams.get('invoice') || '').trim().toLowerCase();
    if (memberQ && !isMembre && selectedMember !== memberQ) {
      setSelectedMember(memberQ);
    }
    if (invoiceQ !== 'arrears') return;
    const target = String(
      isMembre
        ? effectivePersonId
        : memberQ || (selectedMember !== 'all' ? selectedMember : '')
    ).trim();
    if (!target) return;
    arrearsInvoiceQueryHandledRef.current = true;
    openArrearsInvoiceForMember(target);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('invoice');
        return next;
      },
      { replace: true }
    );
  }, [
    loading,
    isMembre,
    effectivePersonId,
    selectedMember,
    searchParams,
    setSearchParams,
    contributions,
    memberAllContributions,
  ]);

  const contribPageSizeChoices = useMemo(
    () => [
      { value: '10', label: '10' },
      { value: '20', label: '20' },
      { value: '50', label: '50' },
      { value: '100', label: '100' },
      { value: 'all', label: t('contributionsPage.perPage.all') },
    ],
    [t]
  );

  /** Retard : mois impayés exigibles (après échéance du 15 pour le mois précédent). */
  const arrearUnpaidMonthCount = memberArrearsMonthKeys.length;
  const inArrearsPolicyBand =
    arrearUnpaidMonthCount > 0 &&
    arrearUnpaidMonthCount >= arrearsBandLo &&
    arrearUnpaidMonthCount <= arrearsBandHi;

  /** Montant en attente (membre) = dus/retard + lignes pending hors de cette liste. */
  const memberPendingAmountDisplay = useMemo(() => {
    if (!isMembre) return 0;
    const dueTotal = Number(duePaymentsTotalAmount) || 0;
    const dueKeys = new Set(
      (dueOrOverduePaymentRows || []).map(
        (r) => `${String(r.person_id || '').trim()}:${String(r.month || '').trim().slice(0, 7)}`
      )
    );
    let pendingExtra = 0;
    for (const c of memberAllContributions || []) {
      const st = String(c?.status || '').toLowerCase().trim();
      if (st !== 'pending' && st !== 'awaiting') continue;
      if (isExceptionalMonthValue(c?.month)) continue;
      const ym = String(c?.month || '').trim().slice(0, 7);
      const pid = String(effectivePersonId || '').trim();
      if (dueKeys.has(`${pid}:${ym}`)) continue;
      pendingExtra += Number(c?.amount) || 0;
    }
    return dueTotal + pendingExtra;
  }, [
    isMembre,
    duePaymentsTotalAmount,
    dueOrOverduePaymentRows,
    memberAllContributions,
    effectivePersonId,
  ]);

  /** Conception : bandeaux retard seulement s’il y a un montant en attente (> 0). */
  const showMemberLateNotices =
    isMembre && arrearUnpaidMonthCount > 0 && memberPendingAmountDisplay > 0;

  const memberTotalAmount = isMembre
    ? (memberAllContributions || [])
        .filter((c) => String(c?.status || '').toLowerCase() !== 'rejected')
        .reduce((sum, c) => sum + (Number(c?.amount) || 0), 0)
    : 0;

  const adminCollectedThisMonth = useMemo(() => {
    const rows = adminAllContributions?.length ? adminAllContributions : contributions || [];
    return rows
      .filter((c) => {
        const ym = String(c?.month || '').trim().slice(0, 7);
        return ym === currentMonthKey && paidStatusSet.has(String(c?.status || '').toLowerCase());
      })
      .reduce((s, c) => s + (Number(c?.amount) || 0), 0);
  }, [adminAllContributions, contributions, currentMonthKey, paidStatusSet]);

  /** Paiements en attente = dus/en retard (avis) + cotisations `pending` hors de cette liste. */
  const adminPendingAmountDisplay = useMemo(() => {
    if (isMembre) return null;
    const dueTotal = Number(duePaymentsTotalAmount) || 0;
    const rows = Array.isArray(adminAllContributions) ? adminAllContributions : [];
    const dueKeys = new Set(
      (dueOrOverduePaymentRows || []).map(
        (r) =>
          `${String(r.person_id || '').trim()}:${String(r.month || '').trim().slice(0, 7)}`
      )
    );
    let pendingExtra = 0;
    for (const c of rows) {
      const st = String(c?.status || '').toLowerCase();
      if (st !== 'pending' && st !== 'awaiting') continue;
      const pid = getContributionMemberId(c);
      const ym = String(c?.month || '').trim().slice(0, 7);
      if (dueKeys.has(`${pid}:${ym}`)) continue;
      pendingExtra += Number(c?.amount) || 0;
    }
    // Filet si l’avis n’est pas encore calculé : au moins le pending historique / API
    if (dueTotal === 0 && pendingExtra === 0) {
      if (rows.length > 0) {
        return rows
          .filter((c) => {
            const st = String(c?.status || '').toLowerCase();
            return st === 'pending' || st === 'awaiting';
          })
          .reduce((s, c) => s + (Number(c?.amount) || 0), 0);
      }
      return adminPendingAmountSum;
    }
    return dueTotal + pendingExtra;
  }, [
    isMembre,
    duePaymentsTotalAmount,
    dueOrOverduePaymentRows,
    adminAllContributions,
    adminPendingAmountSum,
  ]);

  /** Total encaissé = paiements validés uniquement. */
  const adminCollectedTotalDisplay = useMemo(() => {
    if (isMembre) return null;
    const rows = Array.isArray(adminAllContributions) ? adminAllContributions : [];
    if (rows.length > 0) {
      return rows
        .filter((c) => paidStatusSet.has(String(c?.status || '').toLowerCase()))
        .reduce((s, c) => s + (Number(c?.amount) || 0), 0);
    }
    return adminTotalAmountSum;
  }, [isMembre, adminAllContributions, adminTotalAmountSum, paidStatusSet]);

  const memberCollectedThisMonth = useMemo(() => {
    if (!isMembre || !effectivePersonId) return 0;
    return (memberAllContributions || [])
      .filter((c) => {
        const ym = String(c?.month || '').trim().slice(0, 7);
        return ym === currentMonthKey && paidStatusSet.has(String(c?.status || '').toLowerCase());
      })
      .reduce((s, c) => s + (Number(c?.amount) || 0), 0);
  }, [isMembre, effectivePersonId, memberAllContributions, currentMonthKey, paidStatusSet]);

  const adminMembersUpToDate = useMemo(() => {
    if (isMembre) return { upToDate: 0, total: 0 };
    const active = (Array.isArray(members) ? members : []).filter((m) =>
      memberIsActiveForArrearsInvoice(m)
    );
    let upToDate = 0;
    for (const m of active) {
      const pid = String(m?.id || '').trim();
      if (!pid) continue;
      const keys = listUnpaidRegularMonthKeys(contributionsScopedToPerson(arrearsSourceRows, pid), {
        personId: pid,
        joinMonth: m?.join_date || m?.membership_date || m?.created_at || '',
        maxLookback: 36,
        paymentDeadlineDay,
      });
      if (keys.length === 0) upToDate += 1;
    }
    return { upToDate, total: active.length };
  }, [isMembre, members, arrearsSourceRows, contributionsScopedToPerson, paymentDeadlineDay]);

  const paymentKpiItems = useMemo(() => {
    if (isMembre) {
      return [
        {
          key: 'total',
          label: t('contributionsPage.kpi.totalCounted'),
          value: formatContributionAmount(memberTotalAmount, localeFmt).replace(/ FBU$/, ''),
          suffix: 'FBU',
          accentClass: 'border-l-4 border-l-[#2f6fd4]',
        },
        {
          key: 'month',
          label: t('contributionsPage.kpi.collectedMonth'),
          value: formatContributionAmount(memberCollectedThisMonth, localeFmt).replace(/ FBU$/, ''),
          suffix: 'FBU',
          sub: t('contributionsPage.kpi.monthSub'),
          accentClass: 'border-l-4 border-l-emerald-500',
        },
        {
          key: 'pending',
          label: t('contributionsPage.kpi.pendingPayments'),
          value: formatContributionAmount(memberPendingAmountDisplay, localeFmt).replace(/ FBU$/, ''),
          suffix: 'FBU',
          sub: t('contributionsPage.kpi.pendingAmountSub'),
          accentClass: 'border-l-4 border-l-amber-500',
        },
        {
          key: 'paidMonths',
          label: t('contributionsPage.kpi.paid'),
          value: String(memberPaidMonthsForKpi),
          accentClass: 'border-l-4 border-l-slate-400',
        },
      ];
    }
    return [
      {
        key: 'total',
        label: t('contributionsPage.kpi.totalList'),
        value:
          adminCollectedTotalDisplay == null
            ? '—'
            : formatContributionAmount(adminCollectedTotalDisplay, localeFmt).replace(/ FBU$/, ''),
        suffix: adminCollectedTotalDisplay == null ? '' : 'FBU',
        accentClass: 'border-l-4 border-l-[#2f6fd4]',
      },
      {
        key: 'month',
        label: t('contributionsPage.kpi.collectedMonth'),
        value: formatContributionAmount(adminCollectedThisMonth, localeFmt).replace(/ FBU$/, ''),
        suffix: 'FBU',
        sub: t('contributionsPage.kpi.monthSub'),
        accentClass: 'border-l-4 border-l-emerald-500',
      },
      {
        key: 'pending',
        label: t('contributionsPage.kpi.pendingPayments'),
        value:
          adminPendingAmountDisplay == null
            ? '—'
            : formatContributionAmount(adminPendingAmountDisplay, localeFmt).replace(/ FBU$/, ''),
        suffix: adminPendingAmountDisplay == null ? '' : 'FBU',
        sub: t('contributionsPage.kpi.pendingAmountSub'),
        accentClass: 'border-l-4 border-l-amber-500',
      },
      {
        key: 'uptodate',
        label: t('contributionsPage.kpi.membersUpToDate'),
        value: String(adminMembersUpToDate.upToDate),
        sub: t('contributionsPage.kpi.membersUpToDateSub', {
          upToDate: adminMembersUpToDate.upToDate,
          total: adminMembersUpToDate.total,
        }),
        accentClass: 'border-l-4 border-l-slate-400',
      },
    ];
  }, [
    isMembre,
    t,
    localeFmt,
    memberTotalAmount,
    memberCollectedThisMonth,
    memberPendingAmountDisplay,
    memberPaidMonthsForKpi,
    adminTotalAmountSum,
    adminCollectedTotalDisplay,
    adminCollectedThisMonth,
    adminPendingAmountSum,
    adminPendingAmountDisplay,
    adminMembersUpToDate,
  ]);

  const activeFilterBadges = useMemo(() => {
    const badges = [];
    if (!isMembre && selectedMember !== 'all') {
      badges.push({
        key: 'member',
        label: t('contributionsPage.filters.member'),
        value: getMemberName(selectedMember),
        onRemove: () => setSelectedMember('all'),
      });
    }
    if (selectedMonth !== 'all') {
      badges.push({
        key: 'month',
        label: t('contributionsPage.filters.month'),
        value: getMonthName(selectedMonth),
        onRemove: () => setSelectedMonth('all'),
      });
    }
    if (selectedStatus !== 'all') {
      const statusLabel =
        selectedStatus === 'due'
          ? t('contributionsPage.filters.statusDue')
          : selectedStatus === 'pending'
            ? t('contributionsPage.filters.statusPending')
            : selectedStatus === 'completed'
              ? t('contributionsPage.filters.statusPaid')
              : selectedStatus === 'rejected'
                ? t('contributionsPage.filters.statusRejected')
                : selectedStatus;
      badges.push({
        key: 'status',
        label: t('contributionsPage.filters.status'),
        value: statusLabel,
        onRemove: () => setSelectedStatus('all'),
      });
    }
    if (searchQuery.trim()) {
      badges.push({
        key: 'search',
        label: t('contributionsPage.filters.searchAdmin').split('…')[0] || 'Recherche',
        value: searchQuery.trim(),
        onRemove: () => setSearchQuery(''),
      });
    }
    return badges;
  }, [
    isMembre,
    selectedMember,
    selectedMonth,
    selectedStatus,
    searchQuery,
    t,
    members,
  ]);

  if (loading) {
    return (
      <div className="min-h-[60vh] space-y-4 bg-[#f7f9fc] p-1 sm:p-0">
        <ContributionListSkeleton />
      </div>
    );
  }

  if (loadError && !contributions.length && !adminAllContributions.length) {
    return (
      <div className="min-h-[60vh] bg-[#f7f9fc] py-8">
        <ContributionListError message={loadError} onRetry={() => loadData(true)} />
      </div>
    );
  }

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const memberProofCount = isMembre
    ? memberAllContributions.filter((c) => Array.isArray(c?.proof_documents) && c.proof_documents.length > 0).length
    : 0;
  const memberRejectedCount = isMembre
    ? memberAllContributions.filter((c) => String(c?.status || '').toLowerCase() === 'rejected').length
    : 0;
  const adminSelectedAmount = !isMembre
    ? displayContributions
        .filter((c) => selectedContributionIds.includes(c?.id))
        .reduce((sum, c) => sum + (Number(c?.amount) || 0), 0)
    : 0;

  const memberHasExceptionalType = (typeKey) =>
    exceptionalEnabled &&
    isMembre &&
    effectivePersonId &&
    (contributions || []).some(
      (c) =>
        getContributionMemberId(c) === effectivePersonId && typeKeyMatchesMonth(c?.month, typeKey)
    );

  const showExceptionalFormFields =
    (selectedContribution && isExceptionalMonthValue(selectedContribution.month)) ||
    (!selectedContribution && formKind === 'exceptional');

  const contributionsTotalPages = Math.max(1, Math.ceil(listTotalItems / effectiveItemsPerPage));

  const renderContributionsPagination = (placement) => {
    if (isAllItems || listTotalItems <= 0) return null;
    return (
      <Pagination
        placement={placement}
        currentPage={contributionsPage}
        totalPages={contributionsTotalPages}
        totalItems={listTotalItems}
        itemsPerPage={effectiveItemsPerPage}
        onPageChange={(page) => {
          setContributionsPage(page);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        pageSizeChoices={contribPageSizeChoices}
        pageSizeSelectValue={itemsPerPageOption}
        onPageSizeSelectChange={(v) => {
          setItemsPerPageOption(v);
          setContributionsPage(1);
        }}
      />
    );
  };

  const resolveMemberRow = (contrib) => {
    const pid = getContributionMemberId(contrib) || String(contrib?.person_id || '').trim();
    const member = (members || []).find((m) => String(m?.id || '').trim() === pid);
    const first = String(member?.first_name || '').trim();
    const last = String(member?.last_name || '').trim();
    const preferredName =
      first && last ? `${first} ${last}` : first || last || '';
    const positionId = String(member?.position_id || '').trim();
    const title = positionId ? positionLabelById.get(positionId) || '' : '';
    return {
      pid,
      name:
        preferredName ||
        contrib.memberName ||
        getContributionMemberName(contrib) ||
        member?.name ||
        getMemberName(pid),
      memberNumber: String(member?.member_number || '').trim(),
      title,
    };
  };

  const openEditContribution = (contrib) => {
    const formattedProofDocs = (contrib.proof_documents || []).map((doc) => {
      if (typeof doc === 'string') return { file_url: doc, description: '' };
      return {
        file_url: doc.file_url || doc.url || '',
        description: doc.description || '',
      };
    });
    setSelectedContribution(contrib._virtual ? null : contrib);
    setFormKind(isExceptionalMonthValue(contrib.month) ? 'exceptional' : 'monthly');
    setFormData({
      member_id: getContributionMemberId(contrib) || contrib.person_id || '',
      person_id: getContributionMemberId(contrib) || contrib.person_id || '',
      amount: contrib.amount,
      month: contrib.month,
      status: contrib.status || 'pending',
      payment_method: contrib.payment_method || defaultPaymentMethod,
      notes: contrib.notes || '',
      proof_documents: formattedProofDocs,
    });
    setUploadedFiles(formattedProofDocs);
    setShowForm(true);
  };

  const contributionHasProof = (contrib) =>
    Array.isArray(contrib?.proof_documents) && contrib.proof_documents.length > 0;

  return (
    <div
      className={`min-h-[60vh] bg-[#f7f9fc] ${
        isMembre
          ? 'md:space-y-4'
          : 'space-y-2 md:space-y-4'
      } ${
        isMembre
          ? 'pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] md:pb-0'
          : canMemberCreate
            ? 'pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] md:pb-0'
            : 'pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] md:pb-0'
      }`}
    >
      {isMembre ? (
        <div className="md:hidden">
          <MemberContributionsMobile
            localeFmt={localeFmt}
            amountDue={memberPendingAmountDisplay}
            unpaidMonths={Math.max(arrearUnpaidMonthCount, memberUnpaidMonthsForKpi)}
            deadlineDay={memberPaymentDeadline?.paymentDeadlineDay ?? null}
            daysLeft={memberPaymentDeadline?.daysLeft ?? null}
            dueRows={dueOrOverduePaymentRows}
            contributions={memberAllContributions?.length ? memberAllContributions : contributions}
            canCreate={canMemberCreate}
            canOfferArrearsInvoice={canOfferArrearsInvoice}
            getMonthName={getMonthName}
            onPay={openCreatePaymentForm}
            onPayDue={openRecordPaymentForDue}
            onInvoice={(row) =>
              openContributionInvoice(
                row,
                getContributionMemberName(row) || getMemberName(row?.person_id || effectivePersonId)
              )
            }
            onArrearsInvoice={() => openArrearsInvoiceForMember(effectivePersonId)}
            onReceipt={(row) => openContributionReceipt(row, getContributionMemberName(row))}
            onEdit={(contrib) => {
              setSelectedContribution(contrib);
              const formattedProofDocs = (contrib.proof_documents || []).map((doc) => {
                if (typeof doc === 'string') return { file_url: doc, description: '' };
                return {
                  file_url: doc.file_url || doc.url || '',
                  description: doc.description || '',
                };
              });
              setFormData({
                member_id: getContributionMemberId(contrib),
                person_id: getContributionMemberId(contrib),
                amount: contrib.amount,
                month: contrib.month,
                status: contrib.status,
                payment_method: contrib.payment_method || '',
                notes: contrib.notes || '',
                proof_documents: formattedProofDocs,
              });
              setFormKind(isExceptionalMonthValue(contrib.month) ? 'exceptional' : 'monthly');
              setUploadedFiles(formattedProofDocs);
              setShowForm(true);
            }}
          />
        </div>
      ) : null}

      <div className={isMembre ? 'hidden md:block space-y-4' : 'contents'}>
      {/* Header */}
      <div>
      <PageHeader
        title={
          isMembre ? (
            t('contributionsPage.page.titleMember')
          ) : (
            <span className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              <span>{t('contributionsPage.page.titleAdmin')}</span>
              {adminPendingAmountDisplay != null && adminPendingAmountDisplay > 0 && (
                <span
                  className="inline-flex w-fit items-center rounded-full border border-amber-200/90 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-950 tabular-nums ring-1 ring-amber-100/80"
                  title={t('contributionsPage.page.arrearsBadgeTitle')}
                >
                  {t('contributionsPage.page.arrearsBadge', {
                    amount: adminPendingAmountDisplay.toLocaleString(localeFmt),
                  })}
                </span>
              )}
            </span>
          )
        }
        subtitle={
          isMembre
            ? t('contributionsPage.page.subtitleMember')
            : financialSource === 'ledger'
              ? t('contributionsPage.page.subtitleAdminLedger')
              : t('contributionsPage.page.subtitleAdminEntrea')
        }
        actions={
          <div className="flex w-full flex-wrap items-stretch gap-2 max-md:flex-col md:w-auto md:flex-row md:items-center md:gap-2 lg:gap-3">
            {canExportContributionReport && (
              <UiSurface id="contributions.period_report">
                <button
                  type="button"
                  onClick={() => setShowPeriodReportModal(true)}
                  className="mm-cta mm-cta--ghost min-h-[44px] whitespace-nowrap px-4 text-sm"
                >
                  {t('contributionsPage.page.reportBtn')}
                </button>
              </UiSurface>
            )}
            {!isMembre && hasPermission('contributions.create') && (
              <>
                <input
                  ref={contributionsImportInputRef}
                  type="file"
                  accept=".csv,.txt,.xls,.xlsx"
                  className="hidden"
                  onChange={handleImportContributionsFile}
                />
                <UiSurface id="contributions.import">
                  <button
                    type="button"
                    onClick={() => {
                      if (!ledgerPmLoading && paymentMethods.length === 0) {
                        setShowLedgerSetupModal(true);
                        return;
                      }
                      contributionsImportInputRef.current?.click();
                    }}
                    disabled={importingContributions}
                    className="mm-cta mm-cta--ghost min-h-[44px] whitespace-nowrap px-4 text-sm disabled:opacity-60"
                  >
                    {importingContributions ? t('contributionsPage.page.importRunning') : t('contributionsPage.page.importBtn')}
                  </button>
                </UiSurface>
                <UiSurface id="contributions.import_template">
                  <button
                    type="button"
                    onClick={downloadContributionsImportTemplate}
                    className="mm-cta mm-cta--ghost min-h-[44px] whitespace-nowrap px-4 text-sm"
                  >
                    {t('contributionsPage.page.templateCsv')}
                  </button>
                </UiSurface>
              </>
            )}
            {!isMembre && hasPermission('contributions.bulk_create') && (
              <UiSurface id="contributions.bulk_create">
                <button
                  onClick={() => {
                    if (!ledgerPmLoading && paymentMethods.length === 0) {
                      setShowLedgerSetupModal(true);
                      return;
                    }
                    resetBulkForm();
                    setShowBulkForm(true);
                  }}
                  className="mm-cta mm-cta--ghost min-h-[44px] px-4 text-sm"
                >
                  {t('contributionsPage.page.bulkCreate')}
                </button>
              </UiSurface>
            )}
            {(hasPermission('contributions.create') || (isMembre && hasPermission('contributions.create_own'))) && (
              <button
                onClick={openCreatePaymentForm}
                className="mm-cta min-h-[44px] whitespace-nowrap px-4 text-sm"
              >
                {isMembre ? t('contributionsPage.page.newMember') : t('contributionsPage.page.newAdmin')}
              </button>
            )}
          </div>
        }
      />
      </div>

      {isMembre && (
        <div className="md:hidden flex items-center gap-2 rounded-lg border border-slate-200/90 bg-white px-2.5 py-1.5">
          <p className="min-w-0 flex-1 truncate text-[11px] leading-none text-slate-700 tabular-nums">
            <span className="font-semibold text-[#0f3350]">{memberTotalAmount.toLocaleString(localeFmt)} FBU</span>
            <span className="mx-1 text-slate-300">·</span>
            <span className="text-emerald-800">
              {t('contributionsPage.intro.paidShort', { count: memberPaidMonthsForKpi })}
            </span>
            <span className="mx-1 text-slate-300">·</span>
            <span className="text-amber-800">
              {t('contributionsPage.intro.lateShort', { count: memberUnpaidMonthsForKpi })}
            </span>
            {memberRejectedCount > 0 ? (
              <>
                <span className="mx-1 text-slate-300">·</span>
                <span className="text-red-800">{t('contributionsPage.intro.rejectedShort', { count: memberRejectedCount })}</span>
              </>
            ) : null}
          </p>
          {canOfferArrearsInvoice && (
            <button
              type="button"
              onClick={() => openArrearsInvoiceForMember(effectivePersonId)}
              className="shrink-0 min-h-[32px] rounded-md border border-amber-300/80 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-950"
            >
              {t('contributionsPage.invoice.openArrearsTiny')}
              {memberArrearsMonthKeys.length > 0 ? ` · ${memberArrearsMonthKeys.length}` : ''}
            </button>
          )}
        </div>
      )}

      <UiSurface id="contributions.kpi_grid">
        <ContributionPaymentsKpiGrid items={paymentKpiItems} />
      </UiSurface>

      {!isMembre && hasPermission('contributions.approve') && adminPendingApprovalCount > 0 && (
        <div className="entrea-portal-panel border-l-4 border-l-orange-500 bg-orange-50/90 px-2.5 py-1.5 sm:px-5 md:py-3">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            <p className="text-[11px] leading-snug text-orange-950 md:text-sm">
              <span className="font-semibold">{t('contributionsPage.approvals.bannerTitle')}</span>{' '}
              {t('contributionsPage.approvals.bannerBody', { count: adminPendingApprovalCount })}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setSelectedStatus('pending');
                  setContributionsPage(1);
                }}
                className="shrink-0 min-h-[36px] rounded-lg border border-orange-400 bg-white px-2.5 py-1.5 text-xs font-semibold text-orange-950 hover:bg-orange-100 md:min-h-[40px] md:px-3 sm:text-sm"
              >
                {t('contributionsPage.approvals.showPending')}
              </button>
              <Link
                to="/notifications?tab=approvals"
                className="shrink-0 inline-flex min-h-[36px] items-center rounded-lg bg-orange-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-orange-700 md:min-h-[40px] md:px-3 sm:text-sm"
              >
                {t('contributionsPage.approvals.openInbox')}
              </Link>
            </div>
          </div>
        </div>
      )}

      {showMemberLateNotices && memberPaymentDeadline && (
        <div className="entrea-portal-panel border-l-4 border-l-[#2f6fd4] bg-[#f8fbff] px-2.5 py-1.5 sm:px-5 md:py-3">
          <p className="text-[11px] leading-snug text-[#0f3350] md:text-sm">
            <span className="md:hidden font-semibold tabular-nums">
              {memberPaymentDeadline.daysLeft === 0
                ? t('contributionsPage.deadline.mobileOverdue')
                : t('contributionsPage.deadline.mobileShort', {
                    days: memberPaymentDeadline.daysLeft,
                    day: memberPaymentDeadline.paymentDeadlineDay,
                  })}
            </span>
            <span className="hidden md:inline">
              <span className="font-semibold">{t('contributionsPage.deadline.intro')}</span>{' '}
              {t('contributionsPage.deadline.daysRemain')}{' '}
              <span className="font-bold tabular-nums text-[#113a5b]">
                {memberPaymentDeadline.daysLeft}{' '}
                {memberPaymentDeadline.daysLeft > 1
                  ? t('contributionsPage.deadline.days')
                  : t('contributionsPage.deadline.day')}
              </span>{' '}
              {t('contributionsPage.deadline.before')} {memberPaymentDeadline.paymentDeadlineDay}
              {t('contributionsPage.deadline.monthNote')}.
            </span>
          </p>
          {memberPaymentDeadline.daysLeft === 0 && (
            <p className="mt-2 hidden text-xs text-amber-800 md:block">{t('contributionsPage.deadline.overdue')}</p>
          )}
        </div>
      )}

      {showMemberLateNotices && inArrearsPolicyBand && (
        <div className="entrea-portal-panel border-l-4 border-l-amber-500 bg-amber-50/90 px-2.5 py-1.5 sm:px-5 md:py-3">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            <p className="text-[11px] leading-snug text-amber-950 md:text-sm">
              <span className="md:hidden font-semibold">
                {t('contributionsPage.arrears.mobileShort', { count: arrearUnpaidMonthCount })}
              </span>
              <span className="hidden md:inline">
                <span className="font-semibold">{t('contributionsPage.arrears.title')}</span>{' '}
                {arrearUnpaidMonthCount === 1
                  ? t('contributionsPage.arrears.bodyOne', {
                      months: arrearUnpaidMonthCount,
                      notice: arrearsNoticeDays,
                      deact: arrearsDeactivationDays,
                    })
                  : t('contributionsPage.arrears.body', {
                      months: arrearUnpaidMonthCount,
                      notice: arrearsNoticeDays,
                      deact: arrearsDeactivationDays,
                    })}
              </span>
            </p>
            {canOfferArrearsInvoice && (
              <button
                type="button"
                onClick={() => openArrearsInvoiceForMember(effectivePersonId)}
                className="hidden shrink-0 min-h-[40px] rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100 sm:inline-flex sm:text-sm md:inline-flex"
              >
                {t('contributionsPage.invoice.openArrearsShort')}
              </button>
            )}
          </div>
        </div>
      )}

      {!isMembre && canOfferArrearsInvoice && (
        <div className="entrea-portal-panel border-l-4 border-l-amber-500 bg-amber-50/90 px-2.5 py-1.5 sm:px-5 md:py-3">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            <p className="text-[11px] leading-snug text-amber-950 md:text-sm">
              <span className="font-semibold">{t('contributionsPage.invoice.arrearsAdminTitle')}</span>{' '}
              {t('contributionsPage.invoice.arrearsAdminBody', {
                name: getMemberName(selectedMember),
                count: adminArrearsMonthKeys.length,
              })}
            </p>
            <button
              type="button"
              onClick={() => openArrearsInvoiceForMember(selectedMember)}
              className="shrink-0 min-h-[36px] rounded-lg border border-amber-400 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100 md:min-h-[40px] md:px-3 sm:text-sm"
            >
              {t('contributionsPage.invoice.openArrearsShort')}
            </button>
          </div>
        </div>
      )}

      <section className={isMembre ? 'entrea-portal-panel px-2.5 py-2 sm:px-6 sm:py-5 md:py-5' : ''}>
        {isMembre && (
          <div className="mb-1.5 md:mb-4">
            <div className="hidden md:flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <h3 className="text-base font-semibold text-entrea-ink">{t('contributionsPage.history.title')}</h3>
              <Link
                to="/transactionDetails/cotisations"
                className="inline-flex min-h-[44px] items-center rounded-lg border border-[#bcd4f6] bg-[#edf4ff] px-3 text-sm font-semibold text-[#113a5b] no-underline hover:bg-[#e4eefb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
              >
                {t('contributionsPage.history.transactions')}
              </Link>
            </div>
            <p className="mt-0.5 hidden text-xs text-slate-600 md:block md:text-sm">{t('contributionsPage.history.hint')}</p>
            <div className="md:hidden flex items-center gap-2">
              <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[
                  { key: 'all', label: t('contributionsPage.chips.all') },
                  { key: 'due', label: dueOrOverduePaymentRows.length
                    ? `${t('contributionsPage.chips.dueShort')} (${dueOrOverduePaymentRows.length})`
                    : t('contributionsPage.chips.dueShort') },
                  { key: 'pending', label: t('contributionsPage.chips.pending') },
                  { key: 'completed', label: t('contributionsPage.chips.completed') },
                  { key: 'rejected', label: t('contributionsPage.chips.rejected') },
                ].map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setSelectedStatus(chip.key)}
                    className={`shrink-0 touch-manipulation rounded-full border px-2.5 py-1 text-[10px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                      selectedStatus === chip.key
                        ? 'border-[#113a5b] bg-[#113a5b] text-white'
                        : 'border-slate-300 bg-white text-slate-700'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <Link
                to="/transactionDetails/cotisations"
                className="shrink-0 text-[11px] font-semibold text-[#113a5b] underline decoration-[#bcd4f6] underline-offset-2"
              >
                {t('contributionsPage.history.transactionsShort')}
              </Link>
            </div>
          </div>
        )}

        {/* Barre de recherche et filtres */}
        <SearchAndFilterBar
          variant="portal"
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchInputClassName="!min-h-[44px] py-2"
          collapseSearchOnMobile
          mobileFiltersToggleLabel={t('contributionsPage.filters.mobileToggle')}
          className="max-md:!space-y-1.5 max-md:!p-2 !space-y-2 !p-3"
          placeholder={isMembre ? t('contributionsPage.filters.searchMember') : t('contributionsPage.filters.searchAdmin')}
          filters={[
            ...(!isMembre ? [{
              key: 'member',
              label: t('contributionsPage.filters.member'),
              type: 'select',
              searchable: true,
              value: selectedMember,
              onChange: setSelectedMember,
              options: [
                { value: 'all', label: t('contributionsPage.filters.allMembers') },
                ...(Array.isArray(members) ? members.map(member => ({ value: member.id, label: member.name })) : [])
              ]
            }] : []),
            {
              key: 'month',
              label: t('contributionsPage.filters.month'),
              type: 'select',
              value: selectedMonth,
              onChange: setSelectedMonth,
              options: [
                { value: 'all', label: isMembre ? t('contributionsPage.filters.allMonthsMember') : t('contributionsPage.filters.allMonthsAdmin') },
                ...Array.from(new Set(
                  isMembre
                  ? contributions
                      .filter((c) => getContributionMemberId(c) === effectivePersonId)
                      .map(c => c.month)
                    : contributions.map(c => c.month)
                )).sort((a, b) => {
                  const ar = monthSortRank(a);
                  const br = monthSortRank(b);
                  if (ar !== br) return sortOrder === 'asc' ? ar - br : br - ar;
                  return sortOrder === 'asc'
                    ? String(a || '').localeCompare(String(b || ''))
                    : String(b || '').localeCompare(String(a || ''));
                }).map(month => ({
                  value: month,
                  label: getMonthName(month)
                }))
              ]
            },
            {
              key: 'month_sort',
              label: t('contributionsPage.filters.sortLabel'),
              type: 'select',
              value: sortOrder,
              onChange: setSortOrder,
              options: [
                { value: 'desc', label: t('contributionsPage.filters.sortDesc') },
                { value: 'asc', label: t('contributionsPage.filters.sortAsc') },
              ],
            },
            {
              key: 'status',
              label: t('contributionsPage.filters.status'),
              type: 'button-group',
              value: selectedStatus,
              onChange: setSelectedStatus,
              ...(isMembre ? { hiddenOnMobile: true } : {}),
              options: [
                { value: 'all', label: t('contributionsPage.filters.statusAll') },
                { value: 'due', label: dueOrOverduePaymentRows.length
                  ? `${t('contributionsPage.filters.statusDue')} (${dueOrOverduePaymentRows.length})`
                  : t('contributionsPage.filters.statusDue') },
                { value: 'pending', label: t('contributionsPage.filters.statusPending') },
                { value: 'completed', label: t('contributionsPage.filters.statusPaid') },
                { value: 'rejected', label: t('contributionsPage.filters.statusRejected') }
              ]
            }
          ]}
        />

        <ContributionActiveFilterBadges
          badges={activeFilterBadges}
          onClearAll={() => {
            setSelectedMember('all');
            setSelectedMonth('all');
            setSelectedStatus('all');
            setSearchQuery('');
          }}
        />

        {dueOrOverduePaymentRows.length > 0 && selectedStatus === 'all' && (
          <p className="mb-2 text-xs font-medium text-amber-900 sm:text-sm">
            {t('contributionsPage.dueSection.tableHint', { count: dueOrOverduePaymentRows.length })}
          </p>
        )}

        {canBulkSelectContributions && selectedContributionIds.length > 0 && (
          <UiSurface id="contributions.bulk_bar">
          <div className="sticky top-2 z-20 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-md sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">
                <span className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#1F4668] text-xs font-bold text-white">
                  {selectedContributionIds.length}
                </span>
                {t('contributionsPage.bulkBar.selected')}
              </p>
              <p className="mt-1 text-xs font-semibold tabular-nums text-slate-600">
                {t('contributionsPage.bulkBar.selectedTotal', {
                  amount: formatContributionAmount(adminSelectedAmount, localeFmt),
                })}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleBulkInvoiceContributions}
                className="min-h-[40px] flex-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-950 transition hover:bg-amber-100 sm:flex-initial sm:text-sm"
              >
                {t('contributionsPage.bulkBar.invoice')}
              </button>
              {hasPermission('contributions.approve') && (
                <button
                  type="button"
                  onClick={handleBulkApproveContributions}
                  className="min-h-[40px] flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 sm:flex-initial sm:text-sm"
                >
                  {t('contributionsPage.bulkBar.approve')}
                </button>
              )}
              {hasPermission('contributions.delete') && (
                <button
                  type="button"
                  onClick={handleBulkDeleteContributions}
                  className="min-h-[40px] flex-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700 sm:flex-initial sm:text-sm"
                >
                  {t('contributionsPage.bulkBar.delete')}
                </button>
              )}
            </div>
          </div>
          </UiSurface>
        )}

        {/* Mobile Cards View */}
        <div className="md:hidden space-y-2 relative touch-pan-y px-0.5">
            {renderContributionsPagination('top')}
            {loadingList && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-entrea-royal"></div>
              </div>
            )}
            {displayContributions.map((contrib) => {
              const isDueRow = Boolean(contrib._duePayment || contrib._virtual || contrib.dueState);
              const st = String(contrib.status || '').toLowerCase();
              const overdue = contrib.dueState === 'overdue';
              const borderAccent =
                isDueRow
                  ? overdue
                    ? 'border-l-red-500'
                    : 'border-l-amber-500'
                  : st === 'completed'
                    ? 'border-l-emerald-500'
                    : st === 'rejected'
                      ? 'border-l-red-500'
                      : 'border-l-amber-400';
              const statusPill =
                isDueRow
                  ? overdue
                    ? 'bg-red-50 text-red-900 ring-1 ring-red-200'
                    : 'bg-amber-50 text-amber-950 ring-1 ring-amber-200'
                  : st === 'completed'
                    ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
                    : st === 'rejected'
                      ? 'bg-red-50 text-red-900 ring-1 ring-red-200'
                      : 'bg-amber-50 text-amber-950 ring-1 ring-amber-200';
              const statusLabel = isDueRow
                ? overdue
                  ? t('contributionsPage.dueSection.overdue')
                  : t('contributionsPage.dueSection.due')
                : contrib.status === 'completed'
                  ? t('contributionsPage.mobile.statusPaid')
                  : contrib.status === 'rejected'
                    ? t('contributionsPage.mobile.statusRejected')
                    : t('contributionsPage.mobile.statusPending');
              const memberRow = resolveMemberRow(contrib);
              const mobileActionBtn =
                'flex-1 min-w-[88px] px-2.5 py-1.5 text-xs font-medium rounded-lg transition min-h-[36px] flex items-center justify-center border-0 shadow-none';

              return (
          <div
            key={contrib.id}
            className={`rounded-xl border border-slate-200/90 bg-white p-2.5 border-l-4 ${borderAccent}`}
          >
            <div className="relative mb-1.5 flex items-start gap-2">
              {canBulkSelectContributions && !contrib._virtual && (
                <TableListCheckbox
                  checked={selectedContributionIds.includes(contrib.id)}
                  onChange={() => toggleContributionSelection(contrib.id)}
                  size="lg"
                  className="mt-0.5"
                  aria-label={t('contributionsPage.mobile.selectContrib')}
                />
              )}
              <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {!isMembre ? (
                    <ContributionMemberCell
                      name={memberRow.name}
                      memberNumber={memberRow.memberNumber}
                      title={memberRow.title}
                      className="mb-1"
                    />
                  ) : null}
                  <p className="text-[11px] font-medium tabular-nums text-slate-600">{getMonthName(contrib.month)}</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums tracking-tight text-[#0f3350]">
                    {(Number(contrib.amount) || 0).toLocaleString(localeFmt)}
                    <span className="ml-1 align-top text-[10px] font-semibold text-slate-500">FBU</span>
                  </p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${statusPill}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
            
            {contrib.status === 'rejected' && contrib.rejection_reason && (
              <div className="mt-1.5 rounded-lg bg-red-50 border border-red-100 px-2.5 py-1.5">
                <p className="text-[11px] text-red-700">
                  {t('contributionsPage.mobile.rejectionReasonLabel')} {contrib.rejection_reason}
                </p>
              </div>
            )}

            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-slate-100 pt-1.5 text-[11px] text-slate-600">
              <span className="font-semibold text-gray-900">{contrib.payment_method || '—'}</span>
              {contrib.proof_documents && contrib.proof_documents.length > 0 ? (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="tabular-nums">{contrib.proof_documents.length}</span>
                </>
              ) : null}
            </div>

            <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1.5 touch-manipulation">
                {isDueRow ? (
                  <>
                    {(isMembre ? canMemberCreate : hasPermission('contributions.create')) && (
                      <button
                        type="button"
                        onClick={() => openRecordPaymentForDue(contrib)}
                        className={`${mobileActionBtn} text-entrea-royal active:text-entrea-navy bg-entrea-panel/50 active:bg-entrea-panel`}
                      >
                        {t('contributionsPage.dueSection.record')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        openContributionInvoice(
                          contrib,
                          getContributionMemberName(contrib) || getMemberName(contrib.person_id)
                        )
                      }
                      className={`${mobileActionBtn} text-amber-800 active:text-amber-950 bg-amber-50/80 active:bg-amber-100`}
                    >
                      {t('contributionsPage.dueSection.invoice')}
                    </button>
                  </>
                ) : (
                  <>
                <button
                  type="button"
                  onClick={() => openContributionReceipt(contrib, getContributionMemberName(contrib))}
                  className={`${mobileActionBtn} text-[#2f6fd4] active:text-entrea-navy bg-[#edf4ff]/70 active:bg-[#e4eefb]`}
                >
                  {t('contributionsPage.receipt.open')}
                </button>
                {isUnpaidContributionStatus(contrib.status) && (
                  <button
                    type="button"
                    onClick={() => openContributionInvoice(contrib, getContributionMemberName(contrib))}
                    className={`${mobileActionBtn} text-amber-800 active:text-amber-950 bg-amber-50/80 active:bg-amber-100`}
                  >
                    {t('contributionsPage.invoice.open')}
                  </button>
                )}
                {!isMembre &&
                  hasPermission('contributions.approve') &&
                  contrib.status === 'pending' && (
                  <button
                    onClick={() => handleApprove(contrib)}
                    className={`${mobileActionBtn} text-green-600 active:text-green-800 bg-green-50/50 active:bg-green-100`}
                  >
                    {t('contributionsPage.mobile.approve')}
                  </button>
                )}
                {((!isMembre &&
                  (hasPermission('contributions.approve') ||
                    hasPermission('contributions.edit') ||
                    hasPermission('contributions.delete'))) ||
                  (isMembre &&
                    canMemberCreate &&
                    ['pending', 'rejected'].includes(String(contrib.status || '').toLowerCase()))) && (
                  <>
                {!isMembre && contributionNeedsComptabilisation(contrib) && (
                  <button
                    type="button"
                    disabled={comptabiliserContribId === String(contrib.id)}
                    onClick={() => handleComptabiliserContribution(contrib)}
                    className={`${mobileActionBtn} text-[#2f6fd4] active:text-entrea-navy bg-[#edf4ff]/70 active:bg-[#e4eefb] disabled:opacity-50`}
                  >
                    {comptabiliserContribId === String(contrib.id)
                      ? t('contributionsPage.mobile.comptabilising')
                      : t('contributionsPage.mobile.comptabiliser')}
                  </button>
                )}
                {(isMembre ? canMemberCreate : hasPermission('contributions.edit')) && (
                  <>
                    {isMembre ? (
                      <button
                        onClick={() => {
                          setSelectedContribution(contrib);
                          // Convertir proof_documents au nouveau format si nécessaire
                          const formattedProofDocs = (contrib.proof_documents || []).map(doc => {
                            if (typeof doc === 'string') {
                              // Ancien format (string), convertir en objet avec description vide
                              return { file_url: doc, description: '' };
                            }
                            // Nouveau format (objet), s'assurer qu'il a les bonnes propriétés
                            return {
                              file_url: doc.file_url || doc.url || '',
                              description: doc.description || ''
                            };
                          });
                          setFormData({
                            member_id: getContributionMemberId(contrib),
                            person_id: getContributionMemberId(contrib),
                            amount: contrib.amount,
                            month: contrib.month,
                            status: contrib.status,
                            payment_method: contrib.payment_method || '',
                            notes: contrib.notes || '',
                            proof_documents: formattedProofDocs
                          });
                          setFormKind(isExceptionalMonthValue(contrib.month) ? 'exceptional' : 'monthly');
                          setUploadedFiles(formattedProofDocs);
                          setShowForm(true);
                        }}
                        className={`${mobileActionBtn} text-entrea-royal active:text-entrea-navy bg-entrea-panel/50 active:bg-entrea-panel`}
                      >
                        {String(contrib.status || '').toLowerCase() === 'rejected'
                          ? t('contributionsPage.mobile.correctShort')
                          : t('contributionsPage.mobile.editShort')}
                      </button>
                    ) : (
                      <TableRowActions
                        onEdit={() => {
                          setSelectedContribution(contrib);
                          // Convertir proof_documents au nouveau format si nécessaire
                          const formattedProofDocs = (contrib.proof_documents || []).map(doc => {
                            if (typeof doc === 'string') {
                              // Ancien format (string), convertir en objet avec description vide
                              return { file_url: doc, description: '' };
                            }
                            // Nouveau format (objet), s'assurer qu'il a les bonnes propriétés
                            return {
                              file_url: doc.file_url || doc.url || '',
                              description: doc.description || ''
                            };
                          });
                          setFormData({
                            member_id: getContributionMemberId(contrib),
                            person_id: getContributionMemberId(contrib),
                            amount: contrib.amount,
                            month: contrib.month,
                            status: contrib.status,
                            payment_method: contrib.payment_method || '',
                            notes: contrib.notes || '',
                            proof_documents: formattedProofDocs
                          });
                          setFormKind(isExceptionalMonthValue(contrib.month) ? 'exceptional' : 'monthly');
                          setUploadedFiles(formattedProofDocs);
                          setShowForm(true);
                        }}
                        onDelete={
                          hasPermission('contributions.delete')
                            ? () => handleDelete(contrib.id)
                            : undefined
                        }
                      />
                    )}
                  </>
                )}
                  </>
                )}
                  </>
                )}
              </div>
          </div>
        );
            })}
        {displayContributions.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
            <p className="text-gray-500 text-sm">
              {selectedStatus === 'due'
                ? !isMembre && selectedMember === 'all'
                  ? t('contributionsPage.dueSection.pickMember')
                  : t('contributionsPage.dueSection.emptyFilter')
                : t('contributionsPage.table.empty')}
            </p>
          </div>
        )}
            {renderContributionsPagination('bottom')}
      </div>

        {/* Desktop Table View */}
        <div className="mm-card relative hidden overflow-hidden md:block">
            {renderContributionsPagination('top')}
            {loadingList && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-sm">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#2f6fd4]"></div>
              </div>
            )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100/90">
            <thead className="bg-gradient-to-b from-[#edf4ff]/80 to-slate-50/90">
              <tr>
                {canBulkSelectContributions && (
                  <th className="sticky left-0 z-40 w-12 min-w-[3rem] max-w-[3rem] bg-gray-50 px-3 py-3 text-left">
                    <TableListCheckbox
                      onChange={handleSelectAllContributions}
                      checked={
                        filteredContributions.length > 0 &&
                        selectedContributionIds.length === filteredContributions.length
                      }
                      aria-label={t('contributionsPage.table.selectAll')}
                    />
                  </th>
                )}
                {!isMembre && (
                  <th
                    className={`sticky z-40 w-[14rem] min-w-[14rem] bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 ${
                      canBulkSelectContributions ? 'left-12' : 'left-0'
                    }`}
                  >
                    {t('contributionsPage.table.member')}
                  </th>
                )}
                <th
                  className={`sticky z-40 whitespace-nowrap bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)] ${
                    isMembre
                      ? 'left-0'
                      : canBulkSelectContributions
                        ? 'left-[17rem]'
                        : 'left-[14rem]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort('month')}
                    className="inline-flex items-center gap-1 transition hover:text-entrea-royal"
                    title={t('contributionsPage.table.sortMonthTitle')}
                  >
                    {t('contributionsPage.table.monthSort')} {renderSortIcon('month')}
                  </button>
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    onClick={() => toggleSort('amount')}
                    className="inline-flex items-center gap-1 hover:text-entrea-royal transition"
                    title={t('contributionsPage.table.sortAmountTitle')}
                  >
                    {t('contributionsPage.table.amountSort')} {renderSortIcon('amount')}
                  </button>
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('contributionsPage.table.status')}
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('contributionsPage.table.method')}
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isMembre ? t('contributionsPage.table.proofsMember') : t('contributionsPage.table.proofsAdmin')}
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('contributionsPage.table.actions')}
                  </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100/90 bg-white/60">
              {displayContributions.map((contrib) => {
                const isDueRow = Boolean(contrib._duePayment || contrib._virtual || contrib.dueState);
                const overdue = contrib.dueState === 'overdue';
                const memberRow = resolveMemberRow(contrib);
                return (
                <tr key={contrib.id} className="group transition hover:bg-[#edf4ff]/55">
                  {canBulkSelectContributions && (
                    <td className="sticky left-0 z-30 w-12 min-w-[3rem] max-w-[3rem] whitespace-nowrap bg-white/95 px-3 py-4 group-hover:bg-[#edf4ff]/80">
                      {!contrib._duePayment && !contrib._virtual ? (
                        <TableListCheckbox
                          checked={selectedContributionIds.includes(contrib.id)}
                          onChange={() => toggleContributionSelection(contrib.id)}
                          aria-label={t('contributionsPage.table.selectRowAria', {
                            month: getMonthName(contrib.month),
                          })}
                        />
                      ) : null}
                    </td>
                  )}
                  {!isMembre && (
                    <td
                      className={`sticky z-20 w-[14rem] min-w-[14rem] bg-white/95 px-4 py-3.5 align-middle group-hover:bg-[#edf4ff]/80 ${
                        canBulkSelectContributions ? 'left-12' : 'left-0'
                      }`}
                    >
                      <ContributionMemberCell
                        name={memberRow.name}
                        memberNumber={memberRow.memberNumber}
                        title={memberRow.title}
                      />
                    </td>
                  )}
                  <td
                    className={`sticky z-20 whitespace-nowrap bg-white/95 px-4 py-4 text-sm text-[#0f3350] shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)] group-hover:bg-[#edf4ff]/80 ${
                      isMembre
                        ? 'left-0'
                        : canBulkSelectContributions
                          ? 'left-[17rem]'
                          : 'left-[14rem]'
                    }`}
                  >
                    {getMonthName(contrib.month)}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {(Number(contrib.amount) || 0).toLocaleString(localeFmt)} FBU
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${
                      isDueRow
                        ? overdue
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                        : contrib.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : contrib.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}>
                      {isDueRow
                        ? overdue
                          ? t('contributionsPage.dueSection.overdue')
                          : t('contributionsPage.dueSection.due')
                        : contrib.status === 'completed'
                        ? t('contributionsPage.table.statusPaidFull')
                        : contrib.status === 'rejected'
                          ? t('contributionsPage.table.statusRejectedFull')
                          : t('contributionsPage.table.statusPendingFull')}
                    </span>
                    {contrib.status === 'rejected' && contrib.rejection_reason && (
                      <p className="text-xs text-red-600 mt-1 max-w-xs truncate" title={contrib.rejection_reason}>
                        {contrib.rejection_reason}
                      </p>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {contrib.payment_method || '-'}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-600">
                    {contrib.proof_documents && contrib.proof_documents.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {contrib.proof_documents.map((doc, idx) => {
                          const docObj = typeof doc === 'string' ? { file_url: doc, description: '' } : doc;
                          return (
                            <div key={idx} className="flex items-center gap-1.5">
                              {docObj.description && (
                                <span className="text-gray-700">{docObj.description}</span>
                              )}
                              <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                              </svg>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      '-'
                    )}
                    {isMembre &&
                      canMemberCreate &&
                      ['pending', 'rejected'].includes(String(contrib.status || '').toLowerCase()) && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedContribution(contrib);
                            // Convertir proof_documents au nouveau format si nécessaire
                            const formattedProofDocs = (contrib.proof_documents || []).map(doc => {
                              if (typeof doc === 'string') {
                                // Ancien format (string), convertir en objet avec description vide
                                return { file_url: doc, description: '' };
                              }
                              // Nouveau format (objet), s'assurer qu'il a les bonnes propriétés
                              return {
                                file_url: doc.file_url || doc.url || '',
                                description: doc.description || ''
                              };
                            });
                            setFormData({
                              member_id: getContributionMemberId(contrib),
                              person_id: getContributionMemberId(contrib),
                              amount: contrib.amount,
                              month: contrib.month,
                              status: contrib.status,
                              payment_method: contrib.payment_method || '',
                              notes: contrib.notes || '',
                              proof_documents: formattedProofDocs
                            });
                            setFormKind(isExceptionalMonthValue(contrib.month) ? 'exceptional' : 'monthly');
                            setUploadedFiles(formattedProofDocs);
                            setShowForm(true);
                          }}
                          className="mt-2 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-entrea-royal hover:bg-entrea-navy rounded-lg transition"
                        >
                          {String(contrib.status || '').toLowerCase() === 'rejected'
                            ? t('contributionsPage.mobile.correctResubmit')
                            : t('contributionsPage.mobile.editProof')}
                        </button>
                      )}
                  </td>
                  {!isMembre && (
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isDueRow ? (
                        <>
                          {hasPermission('contributions.create') && (
                            <button
                              type="button"
                              onClick={() => openRecordPaymentForDue(contrib)}
                              className="text-entrea-royal hover:text-entrea-navy font-medium mr-3"
                            >
                              {t('contributionsPage.dueSection.record')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              openContributionInvoice(
                                contrib,
                                getContributionMemberName(contrib) || getMemberName(contrib.person_id)
                              )
                            }
                            className="text-amber-700 hover:text-amber-900 font-medium"
                          >
                            {t('contributionsPage.dueSection.invoice')}
                          </button>
                        </>
                      ) : (
                        <>
                      <button
                        type="button"
                        onClick={() => openContributionReceipt(contrib, getContributionMemberName(contrib))}
                        className="text-[#2f6fd4] hover:text-entrea-navy font-medium mr-3"
                      >
                        {t('contributionsPage.receipt.open')}
                      </button>
                      {isUnpaidContributionStatus(contrib.status) && (
                        <button
                          type="button"
                          onClick={() => openContributionInvoice(contrib, getContributionMemberName(contrib))}
                          className="text-amber-700 hover:text-amber-900 font-medium mr-3"
                        >
                          {t('contributionsPage.invoice.open')}
                        </button>
                      )}
                      {hasPermission('contributions.approve') && contrib.status === 'pending' && (
                        <button
                          onClick={() => handleApprove(contrib)}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          {t('contributionsPage.mobile.approve')}
                        </button>
                      )}
                      {contributionNeedsComptabilisation(contrib) && (
                        <button
                          type="button"
                          disabled={comptabiliserContribId === String(contrib.id)}
                          onClick={() => handleComptabiliserContribution(contrib)}
                          className="text-[#2f6fd4] hover:text-entrea-navy font-medium ml-3 disabled:opacity-50"
                        >
                          {comptabiliserContribId === String(contrib.id)
                            ? t('contributionsPage.mobile.comptabilising')
                            : t('contributionsPage.mobile.comptabiliser')}
                        </button>
                      )}
                      <TableRowActions
                        onEdit={
                          hasPermission('contributions.edit')
                            ? () => {
                                setSelectedContribution(contrib);
                                // Convertir proof_documents au nouveau format si nécessaire
                                const formattedProofDocs = (contrib.proof_documents || []).map(doc => {
                                  if (typeof doc === 'string') {
                                    // Ancien format (string), convertir en objet avec description vide
                                    return { file_url: doc, description: '' };
                                  }
                                  // Nouveau format (objet), s'assurer qu'il a les bonnes propriétés
                                  return {
                                    file_url: doc.file_url || doc.url || '',
                                    description: doc.description || ''
                                  };
                                });
                                setFormData({
                                  member_id: getContributionMemberId(contrib),
                                  person_id: getContributionMemberId(contrib),
                                  amount: contrib.amount,
                                  month: contrib.month,
                                  status: contrib.status,
                                  payment_method: contrib.payment_method || '',
                                  notes: contrib.notes || '',
                                  proof_documents: formattedProofDocs
                                });
                                setFormKind(isExceptionalMonthValue(contrib.month) ? 'exceptional' : 'monthly');
                                setUploadedFiles(formattedProofDocs);
                                setShowForm(true);
                              }
                            : undefined
                        }
                        onDelete={
                          hasPermission('contributions.delete')
                            ? () => handleDelete(contrib.id)
                            : undefined
                        }
                      />
                        </>
                      )}
                    </td>
                  )}
                  {isMembre && (
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isDueRow ? (
                        <>
                          {canMemberCreate && (
                            <button
                              type="button"
                              onClick={() => openRecordPaymentForDue(contrib)}
                              className="text-entrea-royal hover:text-entrea-navy font-medium mr-3"
                            >
                              {t('contributionsPage.dueSection.record')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              openContributionInvoice(
                                contrib,
                                getContributionMemberName(contrib) || getMemberName(contrib.person_id)
                              )
                            }
                            className="text-amber-700 hover:text-amber-900 font-medium"
                          >
                            {t('contributionsPage.dueSection.invoice')}
                          </button>
                        </>
                      ) : (
                        <>
                      <button
                        type="button"
                        onClick={() => openContributionReceipt(contrib, getContributionMemberName(contrib))}
                        className="text-[#2f6fd4] hover:text-entrea-navy font-medium"
                      >
                        {t('contributionsPage.receipt.open')}
                      </button>
                      {isUnpaidContributionStatus(contrib.status) && (
                        <button
                          type="button"
                          onClick={() => openContributionInvoice(contrib, getContributionMemberName(contrib))}
                          className="text-amber-700 hover:text-amber-900 font-medium ml-3"
                        >
                          {t('contributionsPage.invoice.open')}
                        </button>
                      )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
                );
              })}
              {displayContributions.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      isMembre
                        ? 5
                        : 7 + (canBulkSelectContributions ? 1 : 0)
                    }
                    className="px-4 sm:px-6 py-12 text-center text-sm text-gray-500"
                  >
                    {selectedStatus === 'due'
                      ? !isMembre && selectedMember === 'all'
                        ? t('contributionsPage.dueSection.pickMember')
                        : t('contributionsPage.dueSection.emptyFilter')
                      : t('contributionsPage.table.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {renderContributionsPagination('bottom')}
        </div>
      </section>
      </div>

      {/* Form Modal */}
      {showPayForWhomModal && (
        <ModalShell
          onBackdropClick={() => {
            setShowPayForWhomModal(false);
            setPaymentForWhom(null);
          }}
          maxWidthClass="max-w-md"
          panelClassName="overflow-hidden p-0"
        >
          <div className="mm-modal-header">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]">{t('contributionsPage.pageTitle', 'Cotisations')}</p>
              <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                {t('contributionsPage.payForWhom.title')}
              </h2>
              <p className="mt-1 text-xs leading-snug text-slate-600 sm:text-sm">
                {t('contributionsPage.payForWhom.subtitle')}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 px-4 py-4 sm:px-5">
            <button type="button" onClick={startPaymentForSelf} className="mm-cta w-full">
              {t('contributionsPage.payForWhom.self')}
            </button>
            <button type="button" onClick={startPaymentForOther} className="mm-cta mm-cta--ghost w-full">
              {t('contributionsPage.payForWhom.other')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowPayForWhomModal(false);
                setPaymentForWhom(null);
              }}
              className="min-h-[40px] w-full text-sm font-medium text-slate-500"
            >
              {t('contributionsPage.payForWhom.cancel')}
            </button>
          </div>
        </ModalShell>
      )}

      {showForm && (
        <ModalShell
          maxWidthClass="max-w-2xl"
          panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
          onBackdropClick={() => {
            setShowForm(false);
            resetForm();
          }}
        >
          {isMembre && isMobileSurface ? (
            <MemberPaymentFormMobile
              localeFmt={localeFmt}
              title={
                selectedContribution
                  ? t('contributionsPage.mobileForm.titleEdit')
                  : paymentForWhom === 'other'
                    ? t('contributionsPage.mobileForm.titleOther')
                    : t('contributionsPage.mobileForm.titlePay')
              }
              isEdit={!!selectedContribution}
              payForOther={paymentForWhom === 'other' && !selectedContribution}
              formData={formData}
              setFormData={setFormData}
              formKind={formKind}
              setFormKind={setFormKind}
              exceptionalEnabled={exceptionalEnabled}
              exceptionalTypes={exceptionalTypes}
              memberHasExceptionalType={memberHasExceptionalType}
              formatExceptionalMonthKey={formatExceptionalMonthKey}
              parseExceptionalTypeKeyFromMonth={parseExceptionalTypeKeyFromMonth}
              isExceptionalMonthValue={isExceptionalMonthValue}
              showExceptionalFormFields={showExceptionalFormFields}
              autoAllocatePreview={autoAllocatePreview}
              shouldAutoAllocate={shouldAutoAllocateMonthly}
              monthlyContributionAmount={monthlyContributionAmount}
              lastPaidMonthLabel={
                selectedMemberLastPaidMonth ? getMonthName(selectedMemberLastPaidMonth) : ''
              }
              lastPaidLoading={selectedMemberLastPaidLoading}
              members={members}
              effectivePersonId={effectivePersonId}
              selfDisplayName={(() => {
                const mappedName = Array.isArray(members)
                  ? members.find((m) => String(m?.id || '').trim() === effectivePersonId)?.name
                  : '';
                const normalizedMappedName = String(mappedName || '').trim();
                const normalizedUserName = String(user?.name || '').trim();
                const anon = t('contributionsPage.form.anonymousMemberName').toLowerCase();
                if (normalizedMappedName && normalizedMappedName.toLowerCase() !== anon) {
                  return normalizedMappedName;
                }
                return normalizedUserName || t('contributionsPage.form.you');
              })()}
              formPaymentOptions={formPaymentOptions}
              defaultPaymentMethod={defaultPaymentMethod}
              ledgerPmLoading={ledgerPmLoading}
              paymentMethodsEmpty={!ledgerPmLoading && paymentMethods.length === 0}
              uploadedFiles={uploadedFiles}
              uploading={uploading}
              onFileUpload={handleFileUpload}
              onRemoveFile={removeFile}
              onUpdateFileDescription={updateFileDescription}
              canChangePayForWhom={canMemberPayForOthers && !selectedContribution}
              onChangePayForWhom={() => {
                setShowForm(false);
                setShowPayForWhomModal(true);
              }}
              getMonthName={getMonthName}
              onCancel={() => {
                setShowForm(false);
                resetForm();
              }}
              onSubmit={handleSubmit}
              submitLabel={
                selectedContribution
                  ? t('contributionsPage.form.update')
                  : t('contributionsPage.mobileForm.submit')
              }
            />
          ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mm-modal-header">
              <div className="min-w-0">
                <p className="mm-hero__label text-[#1f6ab8]">{t('contributionsPage.pageTitle', 'Cotisations')}</p>
                <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350] sm:text-xl">
                  {selectedContribution
                    ? isMembre
                      ? t('contributionsPage.form.editMember')
                      : t('contributionsPage.form.editAdmin')
                    : isMembre
                      ? paymentForWhom === 'other'
                        ? t('contributionsPage.form.addForOther')
                        : t('contributionsPage.form.addMember')
                      : t('contributionsPage.form.newAdmin')}
                </h2>
                {!selectedContribution ? (
                  <p className="mt-1 text-xs leading-snug text-slate-600 sm:text-sm">
                    {isMembre && paymentForWhom === 'other'
                      ? t('contributionsPage.form.subtitleOther')
                      : t('contributionsPage.form.subtitle')}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                aria-label={t('contributionsPage.form.closeAria')}
                className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 sm:space-y-4 sm:px-5">
              {exceptionalEnabled && !selectedContribution && (
                <div>
                  <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('contributionsPage.form.requestType')}
                  </p>
                  <div className="grid grid-cols-2 gap-1 rounded-xl bg-white p-1 ring-1 ring-slate-200">
                    <button
                      type="button"
                      onClick={() => {
                        setFormKind('monthly');
                        setFormData((prev) => ({
                          ...prev,
                          month: isExceptionalMonthValue(prev.month) ? '' : prev.month,
                        }));
                      }}
                      className={`min-h-[40px] rounded-lg text-xs font-bold ${
                        formKind === 'monthly'
                          ? 'bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-white'
                          : 'text-slate-600'
                      }`}
                    >
                      {t('contributionsPage.form.monthly')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormKind('exceptional');
                        const pick = exceptionalTypes[0];
                        if (pick) {
                          setFormData((prev) => ({ ...prev, month: formatExceptionalMonthKey(pick.key) }));
                        }
                      }}
                      className={`min-h-[40px] rounded-lg text-xs font-bold ${
                        formKind === 'exceptional'
                          ? 'bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-white'
                          : 'text-slate-600'
                      }`}
                    >
                      {t('contributionsPage.form.exceptional')}
                    </button>
                  </div>
                </div>
              )}

              {exceptionalEnabled &&
                !selectedContribution &&
                formKind === 'exceptional' &&
                exceptionalTypes.length > 1 && (
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      {t('contributionsPage.form.exceptionalType')}
                    </label>
                    <MmSelect
                      value={parseExceptionalTypeKeyFromMonth(formData.month) || ''}
                      onChange={(k) => {
                        if (k) {
                          setFormData((prev) => ({ ...prev, month: formatExceptionalMonthKey(k) }));
                        }
                      }}
                      options={exceptionalTypes.map((exT) => ({
                        value: exT.key,
                        label: exT.label,
                      }))}
                      placeholder={t('contributionsPage.form.exceptionalType')}
                    />
                  </div>
                )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                <div className="md:col-span-2">
                  <p className="mb-1 text-xs text-slate-500">
                    {t('contributionsPage.mobileForm.lastPaid')}{' '}
                    <span className="font-medium text-slate-700">
                      {selectedMemberLastPaidLoading
                        ? '…'
                        : selectedMemberLastPaidMonth
                          ? getMonthName(selectedMemberLastPaidMonth)
                          : t('contributionsPage.mobileForm.noLastPaid')}
                    </span>
                  </p>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('contributionsPage.form.member')}
                  </label>
                  {selectedContribution ? (
                    <input
                      type="text"
                      value={(() => {
                        const resolvedName = getContributionMemberName(selectedContribution);
                        const resolvedId = getContributionMemberId(selectedContribution);
                        if (resolvedName && resolvedName !== t('contributionsPage.unknownMember')) {
                          return resolvedName;
                        }
                        return resolvedId
                          ? t('contributionsPage.form.memberWithId', { id: resolvedId })
                          : t('contributionsPage.form.memberUnknown');
                      })()}
                      className="mm-field bg-slate-50"
                      disabled
                    />
                  ) : !isMembre || (isMembre && paymentForWhom === 'other') ? (
                    <>
                      <MmSelect
                        value={formData.person_id || ''}
                        onChange={(personId) =>
                          setFormData({ ...formData, person_id: personId, member_id: personId })
                        }
                        required
                        searchable
                        placeholder={t('contributionsPage.form.selectMember')}
                        searchPlaceholder={t('contributionsPage.form.searchMember')}
                        options={(Array.isArray(members) ? members : [])
                          .filter((member) => {
                            if (!isMembre) return true;
                            return String(member?.id || '').trim() !== String(effectivePersonId || '').trim();
                          })
                          .map((member) => ({
                            value: String(member.id),
                            label: String(member.name || member.id || '').trim() || String(member.id),
                            hint: [member.email, member.phone].filter(Boolean).join(' · '),
                          }))}
                      />
                      {isMembre && canMemberPayForOthers ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowForm(false);
                            setShowPayForWhomModal(true);
                          }}
                          className="mt-2 text-xs font-semibold text-[#2f6fd4] hover:underline"
                        >
                          {t('contributionsPage.payForWhom.change')}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={(() => {
                          const mappedName = Array.isArray(members)
                            ? members.find((m) => String(m?.id || '').trim() === effectivePersonId)?.name
                            : '';
                          const normalizedMappedName = String(mappedName || '').trim();
                          const normalizedUserName = String(user?.name || '').trim();
                          const anon = t('contributionsPage.form.anonymousMemberName').toLowerCase();
                          if (normalizedMappedName && normalizedMappedName.toLowerCase() !== anon) {
                            return normalizedMappedName;
                          }
                          return normalizedUserName || t('contributionsPage.form.you');
                        })()}
                        className="mm-field bg-slate-50"
                        disabled
                      />
                      {canMemberPayForOthers && !selectedContribution ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowForm(false);
                            setShowPayForWhomModal(true);
                          }}
                          className="mt-2 text-xs font-semibold text-[#2f6fd4] hover:underline"
                        >
                          {t('contributionsPage.payForWhom.change')}
                        </button>
                      ) : null}
                    </>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {showExceptionalFormFields
                      ? t('contributionsPage.form.period')
                      : t('contributionsPage.form.month')}
                    {!showExceptionalFormFields && !selectedContribution ? (
                      <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">
                        {t('contributionsPage.form.startMonthOptional')}
                      </span>
                    ) : null}
                  </label>
                  {showExceptionalFormFields ? (
                    <input
                      type="text"
                      readOnly
                      value={getMonthName(formData.month)}
                      className="mm-field bg-slate-50 text-slate-800"
                    />
                  ) : (
                    <input
                      type="month"
                      value={
                        isRegularMonthKey(String(formData.month || '').trim().slice(0, 7))
                          ? String(formData.month).trim().slice(0, 7)
                          : ''
                      }
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      className="mm-field"
                      disabled={!!selectedContribution}
                    />
                  )}
                  {!showExceptionalFormFields && !selectedContribution ? (
                    <p className="mt-1 text-xs text-slate-500">
                      {shouldAutoAllocateMonthly
                        ? t('contributionsPage.form.monthEmptyAutoHint', {
                            monthly: monthlyContributionAmount.toLocaleString(localeFmt),
                          })
                        : t('contributionsPage.form.monthSelectedHint')}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('contributionsPage.form.amount')}
                  </label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                    className="mm-field"
                    required
                    min="0"
                    step={monthlyContributionAmount > 0 ? monthlyContributionAmount : undefined}
                  />
                  {!showExceptionalFormFields && monthlyContributionAmount > 0 ? (
                    <p className="mt-1 text-xs text-slate-500">
                      {t('contributionsPage.form.amountMultipleHint', {
                        monthly: monthlyContributionAmount.toLocaleString(localeFmt),
                      })}
                    </p>
                  ) : null}
                </div>

                {!showExceptionalFormFields && !selectedContribution && autoAllocatePreview.enabled ? (
                  <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                    <div className="rounded-md border border-slate-100 bg-white p-2.5 text-xs text-slate-700">
                      {autoAllocatePreview.error === 'monthly_not_configured' ? (
                        <p>{t('contributionsPage.form.autoAllocateNoMonthlyAmount')}</p>
                      ) : autoAllocatePreview.error === 'invalid_total' ? (
                        <p>{t('contributionsPage.form.autoAllocateInvalidTotal')}</p>
                      ) : autoAllocatePreview.rows.length > 0 ? (
                        <>
                          <p className="font-semibold text-slate-800">
                            {t('contributionsPage.form.autoPreviewTitle', {
                              count: autoAllocatePreview.rows.length,
                              start: getMonthName(autoAllocatePreview.startMonth),
                            })}
                          </p>
                          <p className="mt-1">
                            {autoAllocatePreview.rows
                              .slice(0, 6)
                              .map(
                                (r) =>
                                  `${getMonthName(r.month)} (${Number(r.amount).toLocaleString(localeFmt)} FBU)`
                              )
                              .join(' · ')}
                            {autoAllocatePreview.rows.length > 6 ? ' · …' : ''}
                          </p>
                        </>
                      ) : (
                        <p>{t('contributionsPage.form.autoPreviewEmpty')}</p>
                      )}
                    </div>
                  </div>
                ) : null}

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('contributionsPage.form.status')}
                  </label>
                  {isMembre ? (
                    <input
                      type="text"
                      value={
                        paymentForWhom === 'other' && !selectedContribution
                          ? t('contributionsPage.form.statusPaid')
                          : t('contributionsPage.form.memberPendingLabel')
                      }
                      className="mm-field bg-slate-50"
                      disabled
                    />
                  ) : (
                    <MmSelect
                      value={formData.status}
                      onChange={(status) => setFormData({ ...formData, status })}
                      required
                      options={[
                        { value: 'pending', label: t('contributionsPage.form.statusPending') },
                        { value: 'completed', label: t('contributionsPage.form.statusPaid') },
                      ]}
                      placeholder={t('contributionsPage.form.status')}
                    />
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('contributionsPage.form.paymentMethod')}
                  </label>
                  <MmSelect
                    value={formData.payment_method || defaultPaymentMethod}
                    onChange={(payment_method) => setFormData({ ...formData, payment_method })}
                    required
                    disabled={formPaymentOptions.length === 0}
                    options={formPaymentOptions.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    placeholder={t('contributionsPage.form.paymentMethod')}
                  />
                  {ledgerPmLoading &&
                    !(Array.isArray(settings?.financial?.payment_methods) && settings.financial.payment_methods.length > 0) && (
                    <p className="mt-1 text-xs text-slate-500">{t('contributionsPage.form.pmLoadingHint')}</p>
                  )}
                  {!ledgerPmLoading && paymentMethods.length === 0 && (
                    <p className="mt-1 text-xs text-amber-700">{t('contributionsPage.form.pmNone')}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('contributionsPage.form.notes')}
                </label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mm-field"
                  rows={3}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {isMembre
                    ? t('contributionsPage.form.proofsMember')
                    : t('contributionsPage.form.proofsAdmin')}
                </label>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="block w-full cursor-pointer text-sm text-slate-500 file:mr-3 file:rounded-xl file:border-0 file:bg-[#edf4ff] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-[#113a5b]"
                  />
                  <p className="mt-1.5 text-xs text-slate-500">{t('contributionsPage.form.formatsHint')}</p>

                  {uploading ? (
                    <div className="mt-2 text-sm font-medium text-[#2f6fd4]">
                      {t('contributionsPage.form.uploading')}
                    </div>
                  ) : null}

                  {uploadedFiles.length > 0 ? (
                    <div className="mt-3 space-y-2.5">
                      {uploadedFiles.map((fileItem) => {
                        const fileUrl = typeof fileItem === 'string' ? fileItem : fileItem.file_url;
                        const fileName = fileUrl.split('/').pop();
                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fileName);
                        const description = typeof fileItem === 'string' ? '' : (fileItem.description || '');
                        return (
                          <div
                            key={fileUrl}
                            className="rounded-xl border border-slate-200/90 bg-slate-50/80 p-3"
                          >
                            <div className="mb-2 flex items-start justify-between gap-2">
                              <div className="flex min-w-0 flex-1 items-center gap-2">
                                {isImage ? (
                                  <svg className="h-5 w-5 shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5 shrink-0 text-[#2f6fd4]" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                                  </svg>
                                )}
                                <span className="truncate text-sm text-slate-700">{fileName}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(fileUrl)}
                                className="mm-press shrink-0 text-red-500"
                              >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            <label className="mb-1 block text-xs font-medium text-slate-600">
                              {t('contributionsPage.form.descLabel')}{' '}
                              <span className="text-red-500">{t('contributionsPage.form.descRequired')}</span>
                            </label>
                            <input
                              type="text"
                              value={description}
                              onChange={(e) => updateFileDescription(fileUrl, e.target.value)}
                              placeholder={t('contributionsPage.form.descPlaceholder')}
                              className="mm-field !min-h-[42px] text-sm"
                              required
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
              </div>

              <div className="mm-modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="mm-cta mm-cta--ghost min-h-[44px] flex-1 sm:flex-none"
                >
                  {t('contributionsPage.form.cancel')}
                </button>
                <button type="submit" className="mm-cta min-h-[44px] flex-1 sm:flex-none sm:min-w-[8rem]">
                  {selectedContribution ? t('contributionsPage.form.update') : t('contributionsPage.form.create')}
                </button>
              </div>
            </form>
          </div>
          )}
        </ModalShell>
      )}

      {/* Bulk Form Modal */}
      {showBulkForm && (
        <ModalShell
          maxWidthClass="max-w-2xl"
          panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
          onBackdropClick={() => {
            setShowBulkForm(false);
            resetBulkForm();
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mm-modal-header">
              <div className="min-w-0">
                <p className="mm-hero__label text-[#1f6ab8]">{t('contributionsPage.pageTitle', 'Cotisations')}</p>
                <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350] sm:text-xl">
                  {t('contributionsPage.bulk.title')}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => { setShowBulkForm(false); resetBulkForm(); }}
                aria-label={t('contributionsPage.bulk.closeAria')}
                className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleBulkSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 sm:space-y-4 sm:px-5">
              {exceptionalEnabled && exceptionalTypes.length > 0 && (
                <div>
                  <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('contributionsPage.bulk.requestType')}
                  </p>
                  <div className="grid grid-cols-2 gap-1 rounded-xl bg-white p-1 ring-1 ring-slate-200">
                    <button
                      type="button"
                      onClick={() => {
                        setBulkFormKind('monthly');
                        setBulkData((prev) => ({
                          ...prev,
                          month: isExceptionalMonthValue(prev.month) ? '' : prev.month,
                        }));
                      }}
                      className={`min-h-[40px] rounded-lg text-xs font-bold ${
                        bulkFormKind === 'monthly'
                          ? 'bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-white'
                          : 'text-slate-600'
                      }`}
                    >
                      {t('contributionsPage.bulk.monthly')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBulkFormKind('exceptional');
                        const pick = exceptionalTypes[0];
                        if (pick) {
                          setBulkData((prev) => ({ ...prev, month: formatExceptionalMonthKey(pick.key) }));
                        }
                      }}
                      className={`min-h-[40px] rounded-lg text-xs font-bold ${
                        bulkFormKind === 'exceptional'
                          ? 'bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-white'
                          : 'text-slate-600'
                      }`}
                    >
                      {t('contributionsPage.bulk.exceptional')}
                    </button>
                  </div>
                </div>
              )}

              <div className="rounded-2xl bg-white/90 p-3 ring-1 ring-slate-200/80">
                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('contributionsPage.bulk.membersLabel')}
                  </label>
                  {allBulkMemberIds.length > 0 ? (
                    <label className="flex min-h-[40px] cursor-pointer items-center gap-2 text-sm text-slate-700 sm:min-h-0">
                      <input
                        ref={bulkSelectAllRef}
                        type="checkbox"
                        className="h-5 w-5 shrink-0 rounded border-slate-300 text-[#2f6fd4] focus:ring-[#2f6fd4]/40"
                        checked={
                          allBulkMemberIds.length > 0 &&
                          bulkData.member_ids.length === allBulkMemberIds.length
                        }
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setBulkData((prev) => ({
                            ...prev,
                            member_ids: checked ? [...allBulkMemberIds] : [],
                          }));
                        }}
                        aria-label={t('contributionsPage.bulk.selectAllAria')}
                      />
                      <span className="font-medium">{t('contributionsPage.bulk.selectAllMembers')}</span>
                    </label>
                  ) : null}
                </div>
                <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200/90 bg-slate-50/50 p-2">
                  {members.map((member) => (
                    <label
                      key={member.id}
                      className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg p-2.5 hover:bg-white/80"
                    >
                      <TableListCheckbox
                        checked={bulkData.member_ids.includes(member.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBulkData({ ...bulkData, member_ids: [...bulkData.member_ids, member.id] });
                          } else {
                            setBulkData({
                              ...bulkData,
                              member_ids: bulkData.member_ids.filter((id) => id !== member.id),
                            });
                          }
                        }}
                        size="lg"
                        aria-label={t('contributionsPage.bulk.includeMemberAria', { name: member.name })}
                      />
                      <span className="text-sm font-medium text-[#0f3350]">{member.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                {bulkFormKind === 'exceptional' && exceptionalEnabled && exceptionalTypes.length > 0 ? (
                  exceptionalTypes.length > 1 ? (
                    <div>
                      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                        {t('contributionsPage.bulk.exceptionalType')}
                      </label>
                      <MmSelect
                        value={parseExceptionalTypeKeyFromMonth(bulkData.month) || ''}
                        onChange={(k) => {
                          if (k) {
                            setBulkData((prev) => ({ ...prev, month: formatExceptionalMonthKey(k) }));
                          }
                        }}
                        required
                        options={exceptionalTypes.map((exT) => ({
                          value: exT.key,
                          label: exT.label,
                        }))}
                        placeholder={t('contributionsPage.bulk.exceptionalType')}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                        {t('contributionsPage.bulk.exceptionalType')}
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={exceptionalTypes[0]?.label || ''}
                        className="mm-field bg-slate-50 text-slate-800"
                      />
                    </div>
                  )
                ) : (
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      {t('contributionsPage.bulk.month')}
                    </label>
                    <input
                      type="month"
                      value={bulkData.month}
                      onChange={(e) => setBulkData({ ...bulkData, month: e.target.value })}
                      className="mm-field"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('contributionsPage.bulk.amount')}
                  </label>
                  <input
                    type="number"
                    value={bulkData.amount}
                    onChange={(e) => setBulkData({ ...bulkData, amount: parseFloat(e.target.value) })}
                    className="mm-field"
                    required
                    min="0"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('contributionsPage.bulk.paymentMethod')}
                  </label>
                  <MmSelect
                    value={bulkData.payment_method || defaultPaymentMethod}
                    onChange={(payment_method) => setBulkData({ ...bulkData, payment_method })}
                    required
                    disabled={bulkPaymentOptions.length === 0}
                    options={bulkPaymentOptions.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    placeholder={t('contributionsPage.bulk.paymentMethod')}
                  />
                  {ledgerPmLoading &&
                    !(Array.isArray(settings?.financial?.payment_methods) && settings.financial.payment_methods.length > 0) && (
                    <p className="mt-1 text-xs text-slate-500">{t('contributionsPage.bulk.pmLoadingHint')}</p>
                  )}
                  {!ledgerPmLoading && paymentMethods.length === 0 && (
                    <p className="mt-1 text-xs text-amber-700">{t('contributionsPage.bulk.pmNone')}</p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('contributionsPage.bulk.paymentDate')}
                  </label>
                  <input
                    type="date"
                    value={bulkData.payment_date || ''}
                    onChange={(e) =>
                      setBulkData((prev) => ({ ...prev, payment_date: e.target.value || '' }))
                    }
                    className="mm-field max-w-xs"
                  />
                  <p className="mt-1 text-xs text-slate-500">{t('contributionsPage.bulk.paymentDateHelp')}</p>
                </div>
              </div>
              </div>

              <div className="mm-modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowBulkForm(false); resetBulkForm(); }}
                  className="mm-cta mm-cta--ghost min-h-[44px] flex-1 sm:flex-none"
                >
                  {t('contributionsPage.bulk.cancel')}
                </button>
                <button type="submit" className="mm-cta min-h-[44px] flex-1 sm:flex-none sm:min-w-[8rem]">
                  {t('contributionsPage.bulk.create')}
                </button>
              </div>
            </form>
          </div>
        </ModalShell>
      )}

      {showLedgerSetupModal && (
        <ModalShell
          maxWidthClass="max-w-lg"
          panelClassName="overflow-hidden p-0"
          onBackdropClick={() => setShowLedgerSetupModal(false)}
        >
          <div className="mm-modal-header">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]">{t('contributionsPage.pageTitle', 'Cotisations')}</p>
              <h3 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                {t('contributionsPage.ledgerModal.title')}
              </h3>
              <p className="mt-1 text-sm leading-snug text-slate-600">{t('contributionsPage.ledgerModal.body')}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowLedgerSetupModal(false)}
              className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              aria-label={t('contributionsPage.ledgerModal.closeAria')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mm-modal-footer">
            <button
              type="button"
              onClick={() => setShowLedgerSetupModal(false)}
              className="mm-cta mm-cta--ghost flex-1"
            >
              {t('contributionsPage.ledgerModal.close')}
            </button>
            <Link
              to="/settings"
              onClick={() => setShowLedgerSetupModal(false)}
              className="mm-cta flex-1 text-center"
            >
              {t('contributionsPage.ledgerModal.settings')}
            </Link>
          </div>
        </ModalShell>
      )}

      <ContributionPeriodReportModal
        open={showPeriodReportModal}
        onClose={() => setShowPeriodReportModal(false)}
        contributions={adminAllContributions}
        members={members}
        getContributionMemberId={getContributionMemberId}
        getContributionMemberName={getContributionMemberName}
        getMonthName={getMonthName}
        orgName={getResolvedPortalTitle(settings)}
        localeFmt={localeFmt}
        initialPersonId={selectedMember !== 'all' ? selectedMember : ''}
      />

      <ContributionReceiptModal
        open={!!receiptData}
        onClose={() => setReceiptData(null)}
        settings={settings}
        orgName={getResolvedPortalTitle(settings)}
        logoUrl={String(settings?.organization?.logo_url || '').trim()}
        memberName={receiptData?.memberName || ''}
        memberPhone={receiptData?.memberPhone || ''}
        personId={receiptData?.personId || ''}
        contributions={receiptData?.contributions || []}
        issuedAt={receiptData?.issuedAt || ''}
        localeFmt={localeFmt}
        onToast={(kind, message) => {
          if (kind === 'error') error(message);
          else success(message);
        }}
      />

      <ContributionInvoiceModal
        open={!!invoiceData}
        onClose={() => setInvoiceData(null)}
        settings={settings}
        orgName={getResolvedPortalTitle(settings)}
        logoUrl={String(settings?.organization?.logo_url || '').trim()}
        memberName={invoiceData?.memberName || ''}
        memberPhone={invoiceData?.memberPhone || ''}
        contributions={invoiceData?.contributions || []}
        issuedAt={invoiceData?.issuedAt || ''}
        dueAt={invoiceData?.dueAt || ''}
        localeFmt={localeFmt}
        onToast={(kind, message) => {
          if (kind === 'error') error(message);
          else success(message);
        }}
      />
    </div>
  );
};

export default Contributions;
